import {
  CommonHelperFunctions,
  ComponentType,
  DeviceType,
  DragDropInsertMethod,
  EAppSupportType,
  EEnumType,
  EItemDropBehavior,
  EProjectType,
  EPropertyType,
  ESdChangeReason,
  ESymbolCategory,
  IAbsolutePosition,
  IComponent,
  IComponentDefinition,
  IDisplayItem,
  IDisplayItemComponent,
  IDisplayItemProperty,
  IDragDropByCreateItemMessage,
  IDragDropByMoveItemMessage,
  IFixedPosition,
  IInjectDisplayItemToTargetDisplayItemMessage,
  IKuikaTypeResolution,
  IPropertyDefinition,
  IScreenCanvasSize,
  IScreenDesign,
  IScreenDesignDictionary,
  ISymbol,
  DashboardState,
  KAgentHelper,
  PlaceToType,
  RightPanelType,
  SpecialDIIds,
  getReportCanvasSize,
  hasExplicitReportPage,
  isAppSupportTypeOfferedOnScreen
} from "@kuika/kuika-cl-model";
import * as _ from "lodash";
import { isReactNativeGenerationOffered } from "../../components/design-tools/app-settings/react-native-feature-gate";
import { ResponsiveTypes } from "../../components/design-tools/right-panel/properties-panel/responsive-design-toolkit";
import { ScreenDesignContextProvider } from "../../contexts/screen-design-context/screen-design-context-provider";
import { IDisplayItemClipboard } from "../../models/interface/display-item-clipboard";
import { applyChildItemSizeDefaults, isDeclaredChildItem } from "./component-children-host";
import { FIXED_OVERLAY_COMPONENTS } from "./component-drop-constants";
import { KMainFunctions } from "./main-functions";
import { ScreenDesignHelper } from "./screen-design-helper";

declare let window: any;

export interface IEvent {
  detail: {
    action: string;
    reason: string;
    source: { name: string; id: string; kuikaid: string };
    target: { kuikaid: string; colsNumber?: number[] };
    insertAfter?: boolean;
    type: string;
  };
}

export class DragDrop {
  screenContext: ScreenDesignContextProvider;

  constructor(screenContext: ScreenDesignContextProvider) {
    this.screenContext = screenContext;
    if (window.kuika === undefined) {
      window.kuika = {};
    }

    window.testingTools = {};

    window.testingTools.disableParentIdMitigation = () => {
      localStorage.setItem("disableParentIdMitigation", "true");
    };

    window.testingTools.enableParentIdMitigation = () => {
      localStorage.removeItem("disableParentIdMitigation");
    };

    window.testingTools.enableSimulateCorruptedParentId = () => {
      localStorage.setItem("simulateCorruptedParentId", "true");
    };

    window.testingTools.disableSimulateCorruptedParentId = () => {
      localStorage.removeItem("simulateCorruptedParentId");
    };
  }

  cloneAndRemoveDisplayItemByKuikaid = (kuikaid: string, sd: IScreenDesign): IDisplayItem | undefined => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    const di = dict[parseInt(kuikaid, 10)];

    if (!di) {
      return;
    }

    if (di) {
      const newDisplayItem: IDisplayItem = _.cloneDeep(di);
      newDisplayItem.parentId = undefined;

      const parentId = this.getParentId(di, sd.displayItems);

      if (parentId) {
        const parent = dict[parentId];

        if (parent) {
          _.remove(parent.children, di);
        }
      } else if (sd) {
        _.remove(sd.displayItems, di);
        if (sd.displayItems && sd.displayItems.length > 0) {
          for (let index = 0; index < sd.displayItems.length; index++) {
            if (sd.displayItems[index].children) {
              _.remove(sd.displayItems[index].children, di);
            }
          }
        }
      }

      return newDisplayItem;
    }

    return undefined;
  };

  private assignFriendlyNamesBFS = (root: IDisplayItem, existingNames: Set<string>) => {
    const queue: IDisplayItem[] = [root];
    while (queue.length > 0) {
      const current = queue.shift()!;
      const baseName = current?.item?.name || "Component";
      let counter = 1;
      let candidate = `${baseName}${counter}`;
      while (existingNames.has(candidate)) {
        counter += 1;
        candidate = `${baseName}${counter}`;
      }
      current.friendlyName = candidate;
      existingNames.add(candidate);
      if (current.children?.length) {
        queue.push(...current.children);
      }
    }
  };

  wrapDIWithDefaultPanel = (
    newDI: IDisplayItem,
    sd: IScreenDesign,
    doesItNeedColumn: boolean,
    fixedPositionInfo?: IFixedPosition,
    absolutePositionInfo?: IAbsolutePosition,
    isDraggedBetweenElements?: boolean
  ) => {
    window.kuika.isAbsoluteDesign = sd.isAbsoluteDesign;
    if (window.kuika?.isAbsoluteDesign === false || window.kuika?.isAbsoluteDesign === undefined) {
      return newDI;
    }
    // Skip wrapping with default panel in absolute design mode

    // A fixed floating overlay (FAB) is never wrapped — return it untouched, whichever
    // add path called us (canvas drag-drop, item tree, MCP).
    if (FIXED_OVERLAY_COMPONENTS.has(newDI?.item?.name ?? "")) {
      return newDI;
    }

    if (newDI?.styleObj?.position) {
      if (newDI.item.name !== "StaticTable" && newDI.item.name !== "ReportTable") {
        newDI.styleObj.position.width = fixedPositionInfo?.width;
        newDI.styleObj.position.height = fixedPositionInfo?.height;
      }
      newDI.styleObj.position.yAxis = fixedPositionInfo?.top;
      newDI.styleObj.position.xAxis = fixedPositionInfo?.left;
    }
    return newDI;
  };

  wrapDIWithDefaultRow = (
    newDI: IDisplayItem,
    sd: IScreenDesign,
    doesItNeedColumn: boolean = true,
    fixedPositionInfo?: IFixedPosition
  ) => {
    window.kuika.isAbsoluteDesign = sd.isAbsoluteDesign;
    if (window.kuika?.isAbsoluteDesign === true) {
      return newDI;
    }

    // A fixed floating overlay (FAB) is never wrapped in a Row — return it untouched so
    // it goes straight to the root. This is the single chokepoint that prevents the
    // phantom layout row on every add path (canvas drag-drop, item tree, MCP).
    if (FIXED_OVERLAY_COMPONENTS.has(newDI?.item?.name ?? "")) {
      return newDI;
    }

    const rowComp = this.screenContext.state.project.componentSetObj.components.find(
      (x) => x.componentType === ComponentType.responsiveRow
    );

    if (rowComp) {
      const rowDiComp: IDisplayItemComponent = {
        name: rowComp?.name,
        id: rowComp ? rowComp.id?.toString() : "",
        events: [],
        validations: [],
        properties: _.cloneDeep(rowComp?.properties) as any
      };
      const newComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(rowDiComp);

      let rowDI = ScreenDesignHelper.createNewDisplayItemByComponent(
        sd,
        this.screenContext.screenProvider.getScreenDesignDictionary(),
        undefined,
        rowDiComp,
        this.screenContext.state.project.componentSetObj.components
      ) as IDisplayItem;
      if (newComponentDefinition) {
        rowDI = ScreenDesignHelper.addDefaultComponentStylings(
          rowDI,
          newComponentDefinition,
          this.screenContext.defaultStyleProvider
        );
      }

      if (doesItNeedColumn) {
        const colComponent = this.screenContext.state.project.componentSetObj.components.find(
          (x) => x.componentType === ComponentType.responsiveColumn
        );

        const colComp = _.cloneDeep(colComponent);
        if (colComp) {
          colComp.properties.forEach((p) => {
            if (p.type.indexOf(EPropertyType.column) !== -1) {
              if (!p.symbol) {
                p.symbol = { category: ESymbolCategory.fixed } as ISymbol;
              }
              const newVal = KMainFunctions.getResponsiveColumnStringValue(
                p.symbol.fixedValue,
                this.screenContext.state.project.componentSetObj.gridSystemCount
              );
              p.symbol.fixedValue = newVal;
            }
          });
          const colDiComp: IDisplayItemComponent = {
            name: colComp?.name,
            id: colComp ? colComp.id?.toString() : "",
            events: [],
            validations: [],
            properties: _.cloneDeep(colComp?.properties) as any
          };

          let colDI = ScreenDesignHelper.createNewDisplayItemByComponent(
            sd,
            this.screenContext.screenProvider.getScreenDesignDictionary(),
            undefined,
            colDiComp,
            this.screenContext.state.project.componentSetObj.components
          ) as IDisplayItem;
          if (newComponentDefinition) {
            const colComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(colDiComp);
            colDI = ScreenDesignHelper.addDefaultComponentStylings(
              colDI,
              colComponentDefinition!,
              this.screenContext.defaultStyleProvider
            );
          }

          newDI.parentId = colDI.id;
          colDI.parentId = rowDI.id;
          rowDI.children.push(colDI);
          colDI.children.push(newDI);
        }
      } else {
        newDI.parentId = rowDI.id;
        rowDI.children.push(newDI);
      }
      return rowDI;
    }
    return newDI;
  };

  injectDisplayItemToTargetDisplayItem = (message: IInjectDisplayItemToTargetDisplayItemMessage) => {
    const sd = this.screenContext.screenProvider.getScreenDesign();
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();

    let targetKuikaId: string = message.targetId;
    const simpleTargetKuikaId = targetKuikaId.replace("-lastChildItem", "");
    const targetDI = dict[simpleTargetKuikaId];

    if (!sd) {
      throw new Error("Screen design not found");
    }

    if (!targetDI) {
      throw new Error("Target display item not found");
    }

    // Skip ID renewal for Form components since they have predefined IDs in metadata
    // that must match the actual display item IDs
    const isFormComponent = message.displayItem.item?.name === "Form";

    // Renew ids and base names to avoid collisions (skip for Form)
    let di: IDisplayItem = isFormComponent
      ? { ...message.displayItem }
      : ScreenDesignHelper.renewDisplayItem(message.displayItem, dict);

    // Enforce friendlyName format {ComponentName}{OccurrenceCountInScreen} using BFS
    const existingNames = ScreenDesignHelper.collectExistingFriendlyNames(sd.displayItems);
    if (!isFormComponent) {
      this.assignFriendlyNamesBFS(di, existingNames);
    }

    if (
      di.item?.name !== "Table" &&
      di.item?.name !== "StaticTable" &&
      di.item?.name !== "ReportTable" &&
      di.item?.name !== "Header" &&
      di.item?.name !== "Footer" &&
      di.item?.name !== "Panel" &&
      di.item?.name !== "Form" &&
      di.item?.name !== "Row" &&
      di.item?.name !== "FloatButton" &&
      di.item?.name !== "AgentChat"
    ) {
      di = this.wrapDIWithDefaultRow(di, sd, di.item.name !== "Col");
    } else {
      di.parentId = targetDI.id;
    }

    this.screenContext.customBlocksProvider.updateProjectForCustomBlockStyles().finally(() => {
      this.dragDropByMoveItemMessage({
        targetKuikaId: message.targetId,
        sourceDI: di,
        insertMethod: message.insertMethod,
        nextSelectedDiId: di.id.toString(),
        sourceKuikaId: di.id.toString()
      });

      if (message.changeReason === ESdChangeReason.DragDropBlock) {
        this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropBlock, di.id.toString());
      }
    });
  };

  replaceDisplayItemToTargetDisplayItem = (message: IInjectDisplayItemToTargetDisplayItemMessage) => {
    const sd = this.screenContext.screenProvider.getScreenDesign();
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    const displayItems: IDisplayItem[] = sd.displayItems;
    let targetKuikaId: string = message.targetId;
    const simpleTargetKuikaId = targetKuikaId.replace("-lastChildItem", "");
    if (!sd || !this.screenContext || !this.screenContext.state) {
      return;
    }

    let targetDI = dict[parseInt(simpleTargetKuikaId, 10)];

    if (!targetDI) {
      return;
    }

    const parentId: number | undefined = this.getParentId(targetDI, displayItems);

    if (!parentId) {
      return;
    }

    const parentDi = dict[parentId];

    if (!parentDi) {
      return;
    }

    const index: number = parentDi.children.findIndex((x) => x.id === targetDI?.id);

    if (index === -1) {
      return;
    }

    const di = { ...message.displayItem };
    di.parentId = parentId;

    parentDi.children.splice(index, 1, di);
    sd.displayItems = displayItems;
    this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropDi, simpleTargetKuikaId.toString());
  };

  dragDropByCreateItemMessage = (message: IDragDropByCreateItemMessage) => {
    const sd = this.screenContext.screenProvider.getScreenDesign();
    const sdDict = message.shouldRefreshDict
      ? CommonHelperFunctions.createScreenDesignDict(sd)
      : this.screenContext.screenProvider.getScreenDesignDictionary();

    if (message.targetKuikaId === "-1") {
      message.targetKuikaId = "RootLayout";
    }

    let simpleTargetKuikaId = message.targetKuikaId.replace("-lastChildItem", "");

    if (!sd || !this.screenContext || !this.screenContext.state) {
      return;
    }

    let targetDI = sdDict[parseInt(simpleTargetKuikaId, 10)];
    let parentId: number | undefined;

    if (targetDI) {
      parentId = this.getParentId(targetDI, sd.displayItems);
    }

    let parentDi = sdDict[parentId as number];
    const newComponent: IDisplayItemComponent | undefined = ScreenDesignHelper.createNewComponentById(
      message.sourceId,
      this.screenContext
    );
    const newComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(newComponent);
    if (
      targetDI &&
      parentId &&
      targetDI?.item?.name === "Col" &&
      newComponentDefinition &&
      newComponentDefinition.componentType !== ComponentType.responsiveColumn &&
      (message?.insertMethod === DragDropInsertMethod.insertAfter ||
        message?.insertMethod === DragDropInsertMethod.insertBefore)
    ) {
      targetDI = sdDict[parentId as number];
      parentDi = sdDict[parentId as number];
      simpleTargetKuikaId = targetDI?.id.toString() || "";
    }

    // KOA-18016: a before/after drop of a cell element onto a StaticTable/ReportTable column would place
    // it in the column's PARENT (the StaticTableRow) via placeAfterSpecificItem/placeToBeforeItem —
    // orphaning it outside any column and corrupting the table (columns become unselectable, drops snap
    // to the band). Redirect the drop INTO the column so it becomes a proper cell child. Routes to the
    // placeToLastChildItem branch below. Defense-in-depth with the agent-side insertInto fix.
    // Excluded: a column dragged onto a sibling column (a legitimate column REORDER) — redirecting that
    // to insertInto would hit the "column in column" guard and get rejected.
    if (
      targetDI &&
      (targetDI.item?.name === "StaticTableColumn" || targetDI.item?.name === "ReportTableColumn") &&
      newComponentDefinition?.name !== "StaticTableColumn" &&
      newComponentDefinition?.name !== "ReportTableColumn" &&
      (message?.insertMethod === DragDropInsertMethod.insertAfter ||
        message?.insertMethod === DragDropInsertMethod.insertBefore)
    ) {
      message.insertMethod = DragDropInsertMethod.insertInto;
    }

    let targetParentComponent: IDisplayItemComponent | undefined;

    if (parentDi) {
      targetParentComponent = parentDi.item;
    }

    if (!newComponent) {
      return;
    }

    // Apply predefined values from drag/drop message
    if (message.predefinedValues) {
      for (const [key, value] of Object.entries(message.predefinedValues)) {
        const property = newComponent.properties.find((p) => p.name === key);
        if (property) {
          property.symbol.category = ESymbolCategory.fixed;
          property.symbol.fixedValue = value;
          property.symbol.label = "Value";
          property.symbol.description = "Fixed Value";
        }
      }
    }

    // Create dynamic widget properties and events from resolved props
    if (message.widgetProps?.resolvedProps) {
      this.createWidgetDynamicPropertiesAndEvents(newComponent, message.widgetProps.resolvedProps);
    }

    const properties = newComponent.properties;
    const propertyNames = ["value", "label", "title"];

    for (const propertyName of propertyNames) {
      if (properties.find((p) => p.name === propertyName)) {
        this.screenContext.propertyProvider.addItemToExpandedPropertyList(propertyName);

        if (this.screenContext.designSituationProvider.getActiveRightPanel() !== RightPanelType.Properties) {
          this.screenContext.designSituationProvider.setActiveRightPanelByClick(RightPanelType.Properties);
        }

        break;
      }
    }

    let targetComponentParentDefinition: IComponentDefinition | undefined;

    if (targetParentComponent) {
      targetComponentParentDefinition = this.getComponentDefinition(targetParentComponent);
    }

    if (!newComponentDefinition) {
      return;
    }

    const targetComponent: IDisplayItemComponent | undefined = targetDI?.item;
    const targetComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(targetComponent);

    // In report designer, if dragged component is simple and target is simple, use insertAfter instead of insertInto
    if (
      sd.isAbsoluteDesign === true &&
      message.insertMethod === DragDropInsertMethod.insertInto &&
      newComponentDefinition?.componentType === ComponentType.simple &&
      targetComponentDefinition?.componentType === ComponentType.simple
    ) {
      message.insertMethod = DragDropInsertMethod.insertAfter;
    }

    const isElementDraggable = this.getElementIsDroppable(
      targetComponent,
      targetComponentDefinition,
      newComponentDefinition,
      targetDI,
      sd,
      sdDict,
      message,
      undefined,
      simpleTargetKuikaId,
      undefined,
      false,
      true
    );

    if (!isElementDraggable) {
      return;
    }

    const isParentHeaderOrFooter = ScreenDesignHelper.isParentHeaderOrFooter(
      sdDict[parseInt(simpleTargetKuikaId, 10)],
      sd.displayItems
    );

    const isParentLeftOrRightLayout = ScreenDesignHelper.isParentLeftOrRightLayout(
      sdDict[parseInt(simpleTargetKuikaId, 10)],
      sd.displayItems
    );

    if (newComponentDefinition?.name === "Menu") {
      let mode = "vertical";
      if (isParentHeaderOrFooter) {
        mode = "horizontal";
      } else if (isParentLeftOrRightLayout) {
        mode = "vertical";
      }

      properties.forEach((p) => {
        if (p.name === "mode") {
          p.symbol.fixedValue = mode;
        }
      });
    }

    if (newComponentDefinition.componentType === ComponentType.fixedHeader) {
      this.createHeaderDI(message.sourceId);
    } else if (newComponentDefinition.componentType === ComponentType.fixedFooter) {
      this.createFooterDI(message.sourceId);
    } else if (newComponentDefinition.componentType === ComponentType.pageHeader) {
      this.createPageHeaderDI(message.sourceId);
    } else if (newComponentDefinition.componentType === ComponentType.pageFooter) {
      this.createPageFooterDI(message.sourceId);
    } else if (
      message.insertMethod === DragDropInsertMethod.insertBottomOfRootDI ||
      message.insertMethod === DragDropInsertMethod.insertTopOfRootDI
    ) {
      let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
        sd,
        this.screenContext.screenProvider.getScreenDesignDictionary(),
        undefined,
        newComponent,
        this.screenContext.state.project.componentSetObj.components,
        message.fixedPositionInfo,
        message.absolutePositionInfo,
        message.sourceKuikaId
      );
      newDI = ScreenDesignHelper.addDefaultComponentStylings(
        newDI,
        newComponentDefinition,
        this.screenContext.defaultStyleProvider
      );
      ScreenDesignHelper.addChildForItemContainer(
        newComponent,
        newDI,
        sd,
        this.screenContext.screenProvider.getScreenDesignDictionary(),
        this.screenContext.state.project.componentSetObj.components,
        this.screenContext.projectProvider.getProject().projectType,
        this.screenContext.defaultStyleProvider
      );

      if (newDI?.styleObj?.position && sd.isAbsoluteDesign) {
        if (newDI.item.name !== "StaticTable" && newDI.item.name !== "ReportTable") {
          newDI.styleObj.position.width = newDI?.fixedPositionInfo?.width;
          newDI.styleObj.position.height = newDI?.fixedPositionInfo?.height;
        }
        newDI.styleObj.position.yAxis = newDI?.fixedPositionInfo?.top;
        newDI.styleObj.position.xAxis = newDI?.fixedPositionInfo?.left;
      }
      const nextSelectedKuikaId = newDI.id.toString();
      if (
        newComponentDefinition.componentType !== ComponentType.responsiveColumn &&
        newComponentDefinition.componentType !== ComponentType.responsiveRow &&
        newComponentDefinition.name !== "FloatButton" &&
        newComponentDefinition.name !== "AgentChat"
      ) {
        if (sd.isAbsoluteDesign === true) {
          newDI = this.wrapDIWithDefaultPanel(
            newDI,
            sd,
            false,
            message.fixedPositionInfo,
            message.absolutePositionInfo
          );
        } else {
          newDI = this.wrapDIWithDefaultRow(newDI, sd);
        }
      }
      if (message.insertMethod === DragDropInsertMethod.insertBottomOfRootDI) {
        this.placeToRootLastItem(sd, newDI, nextSelectedKuikaId);
      } else {
        this.placeToRootFirstItem(sd, newDI, nextSelectedKuikaId);
      }
    } else if (message.targetKuikaId === "RootLayout") {
      let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
        sd,
        this.screenContext.screenProvider.getScreenDesignDictionary(),
        undefined,
        newComponent,
        this.screenContext.state.project.componentSetObj.components,
        message.fixedPositionInfo,
        message.absolutePositionInfo,
        message.sourceKuikaId
      );
      newDI = ScreenDesignHelper.addDefaultComponentStylings(
        newDI,
        newComponentDefinition,
        this.screenContext.defaultStyleProvider
      );
      ScreenDesignHelper.addChildForItemContainer(
        newComponent,
        newDI,
        sd,
        this.screenContext.screenProvider.getScreenDesignDictionary(),
        this.screenContext.state.project.componentSetObj.components,
        this.screenContext.projectProvider.getProject().projectType,
        this.screenContext.defaultStyleProvider
      );
      if (newDI?.item.name === "Panel" && newDI?.styleObj?.position && sd.isAbsoluteDesign === true) {
        newDI.styleObj.position.height = 100;
      }
      const nextSelectedKuikaId = newDI.id.toString();
      if (
        newComponentDefinition.componentType !== ComponentType.responsiveColumn &&
        newComponentDefinition.componentType !== ComponentType.responsiveRow &&
        newComponentDefinition.name !== "FloatButton" &&
        newComponentDefinition.name !== "AgentChat"
      ) {
        if (sd.isAbsoluteDesign === true) {
          newDI = this.wrapDIWithDefaultPanel(
            newDI,
            sd,
            false,
            message.fixedPositionInfo,
            message.absolutePositionInfo
          );
        } else {
          newDI = this.wrapDIWithDefaultRow(newDI, sd);
        }
      }
      // if (sd.isAbsoluteDesign === true && newDI.item.name === "Panel") {
      //   if (newDI.styleObj?.position && !newDI.styleObj.position.height) {
      //     newDI.styleObj.position.height = 96;
      //   }
      // }
      this.placeToRootLastItem(sd, newDI, nextSelectedKuikaId);
    } else if (simpleTargetKuikaId === SpecialDIIds.rootDI.toString()) {
      targetDI = sdDict[parseInt(simpleTargetKuikaId, 10)];
      if (!targetDI) {
        return;
      }
      let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
        sd,
        this.screenContext.screenProvider.getScreenDesignDictionary(),
        undefined,
        newComponent,
        this.screenContext.state.project.componentSetObj.components,
        message.fixedPositionInfo,
        message.absolutePositionInfo,
        message.sourceKuikaId
      );
      newDI = ScreenDesignHelper.addDefaultComponentStylings(
        newDI,
        newComponentDefinition,
        this.screenContext.defaultStyleProvider
      );

      ScreenDesignHelper.addChildForItemContainer(
        newComponent,
        newDI,
        sd,
        this.screenContext.screenProvider.getScreenDesignDictionary(),
        this.screenContext.state.project.componentSetObj.components,
        this.screenContext.projectProvider.getProject().projectType,
        this.screenContext.defaultStyleProvider
      );
      const nextSelectedKuikaId = newDI.id.toString();
      if (
        newComponentDefinition.componentType !== ComponentType.responsiveColumn &&
        newComponentDefinition.componentType !== ComponentType.responsiveRow
      ) {
        if (sd.isAbsoluteDesign === true) {
          newDI = this.wrapDIWithDefaultPanel(
            newDI,
            sd,
            false,
            message.fixedPositionInfo,
            message.absolutePositionInfo
          );
        } else {
          newDI = this.wrapDIWithDefaultRow(newDI, sd);
        }
        newDI.parentId = targetDI.id;
      }
      if (message.insertMethod === DragDropInsertMethod.insertAfter) {
        this.placeAfterSpecificItem(sd, targetDI, newDI, nextSelectedKuikaId);
      } else {
        if (targetDI.children && targetDI.children.length > 0) {
          targetDI = targetDI.children[0];
        }
        this.placeToBeforeItem(sd, targetDI, newDI, nextSelectedKuikaId);
      }
    } else {
      targetDI = sdDict[parseInt(simpleTargetKuikaId, 10)];
      if (!targetDI) {
        return;
      }
      const targetComponent: IDisplayItemComponent | undefined = targetDI?.item;
      const targetComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(targetComponent);

      if (
        targetComponent &&
        targetComponentDefinition &&
        targetComponentDefinition.componentType === ComponentType.container
      ) {
        if (message.insertMethod === DragDropInsertMethod.insertInto) {
          const isHeightExpandableTarget =
            targetDI?.item?.name === "Header" ||
            targetDI?.item?.name === "Footer" ||
            targetDI?.item?.name === "Page Header" ||
            targetDI?.item?.name === "Page Footer" ||
            targetDI?.item?.name === "Box" ||
            targetDI?.item?.name === "Panel";
          if (isHeightExpandableTarget) {
            const isElementHeightHigherThanContainer = KMainFunctions.getElementHeightIsHigherThanContainer(
              message?.fixedPositionInfo,
              targetDI,
              sd
            );
            if (
              isElementHeightHigherThanContainer &&
              targetDI?.styleObj?.position?.height &&
              message?.fixedPositionInfo?.height
            ) {
              targetDI.styleObj.position.height += message.fixedPositionInfo.height;
            }
          }
          let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
            sd,
            this.screenContext.screenProvider.getScreenDesignDictionary(),
            targetDI?.id,
            newComponent,
            this.screenContext.state.project.componentSetObj.components,
            message.fixedPositionInfo,
            message.absolutePositionInfo,
            message.sourceKuikaId
          );
          newDI = ScreenDesignHelper.addDefaultComponentStylings(
            newDI,
            newComponentDefinition,
            this.screenContext.defaultStyleProvider
          );

          const isWidthDisabled = newComponentDefinition?.inVisibleStyles?.includes("layout_width");
          if (
            targetDI?.item.name === "HorizontalStack" &&
            newDI?.styleObj?.position &&
            !isWidthDisabled &&
            newComponentDefinition?.name !== "Checkbox" &&
            newComponentDefinition?.name !== "Switch"
          ) {
            newDI.styleObj.position.width = 100;
          }

          if (newDI?.styleObj?.position && sd.isAbsoluteDesign) {
            if (newDI.item.name !== "StaticTable" && newDI.item.name !== "ReportTable") {
              newDI.styleObj.position.width = newDI?.fixedPositionInfo?.width;
              newDI.styleObj.position.height = newDI?.fixedPositionInfo?.height;
            }
            newDI.styleObj.position.yAxis = newDI?.fixedPositionInfo?.top;
            newDI.styleObj.position.xAxis = newDI?.fixedPositionInfo?.left;
          }

          ScreenDesignHelper.addChildForItemContainer(
            newComponent,
            newDI,
            sd,
            this.screenContext.screenProvider.getScreenDesignDictionary(),
            this.screenContext.state.project.componentSetObj.components,
            this.screenContext.projectProvider.getProject().projectType,
            this.screenContext.defaultStyleProvider
          );
          this.placeToLastChildItem(sd, targetDI, newDI);
        } else if (message.insertMethod === DragDropInsertMethod.insertAfter) {
          let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
            sd,
            this.screenContext.screenProvider.getScreenDesignDictionary(),
            parentId,
            newComponent,
            this.screenContext.state.project.componentSetObj.components,
            message.fixedPositionInfo,
            message.absolutePositionInfo,
            message.sourceKuikaId
          );

          newDI = ScreenDesignHelper.addDefaultComponentStylings(
            newDI,
            newComponentDefinition,
            this.screenContext.defaultStyleProvider
          );
          const nextSelectedKuikaId = newDI.id.toString();

          ScreenDesignHelper.addChildForItemContainer(
            newComponent,
            newDI,
            sd,
            this.screenContext.screenProvider.getScreenDesignDictionary(),
            this.screenContext.state.project.componentSetObj.components,
            this.screenContext.projectProvider.getProject().projectType,
            this.screenContext.defaultStyleProvider
          );

          if (newDI?.item.name === "Panel" && newDI?.styleObj?.position && sd.isAbsoluteDesign === true) {
            newDI.styleObj.position.height = 100;
          }

          if (sd.isAbsoluteDesign === true && newComponentDefinition.componentType === ComponentType.simple) {
            newDI = this.wrapDIWithDefaultPanel(
              newDI,
              sd,
              false,
              message.fixedPositionInfo,
              message.absolutePositionInfo,
              true
            );
          }

          if (targetDI?.item?.name === "ContentMenuPanel" && parentDi) {
            const parentId = this.getParentId(parentDi, sd.displayItems);
            newDI.parentId = parentId;

            this.placeAfterSpecificItem(sd, parentDi, newDI, nextSelectedKuikaId);
          } else {
            this.placeAfterSpecificItem(sd, targetDI, newDI, nextSelectedKuikaId);
          }
        } else if (message.insertMethod === DragDropInsertMethod.insertBefore) {
          let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
            sd,
            this.screenContext.screenProvider.getScreenDesignDictionary(),
            targetDI.parentId,
            newComponent,
            this.screenContext.state.project.componentSetObj.components,
            message.fixedPositionInfo,
            message.absolutePositionInfo,
            message.sourceKuikaId
          );
          newDI = ScreenDesignHelper.addDefaultComponentStylings(
            newDI,
            newComponentDefinition,
            this.screenContext.defaultStyleProvider
          );
          const nextSelectedKuikaId = newDI.id.toString();
          ScreenDesignHelper.addChildForItemContainer(
            newComponent,
            newDI,
            sd,
            this.screenContext.screenProvider.getScreenDesignDictionary(),
            this.screenContext.state.project.componentSetObj.components,
            this.screenContext.projectProvider.getProject().projectType,
            this.screenContext.defaultStyleProvider
          );
          if (newDI?.item.name === "Panel" && newDI?.styleObj?.position && sd.isAbsoluteDesign === true) {
            newDI.styleObj.position.height = 100;
          }
          if (sd.isAbsoluteDesign === true && newComponentDefinition.componentType === ComponentType.simple) {
            newDI = this.wrapDIWithDefaultPanel(
              newDI,
              sd,
              false,
              message.fixedPositionInfo,
              message.absolutePositionInfo,
              true
            );
          }
          if (targetDI?.item?.name === "ContentMenuPanel" && parentDi) {
            newDI.parentId = parentDi.parentId;
            this.placeToBeforeItem(sd, parentDi, newDI, nextSelectedKuikaId);
          } else {
            this.placeToBeforeItem(sd, targetDI, newDI, nextSelectedKuikaId);
          }
        }
      } else if (
        message.targetKuikaId.includes("-lastChildItem") ||
        message.insertMethod === DragDropInsertMethod.insertInto ||
        (simpleTargetKuikaId === SpecialDIIds.headerDI.toString() &&
          message.insertMethod === DragDropInsertMethod.insertAfter) ||
        (simpleTargetKuikaId === SpecialDIIds.footerDI.toString() &&
          message.insertMethod === DragDropInsertMethod.insertAfter) ||
        (simpleTargetKuikaId === SpecialDIIds.pageHeaderDI.toString() &&
          message.insertMethod === DragDropInsertMethod.insertAfter) ||
        (simpleTargetKuikaId === SpecialDIIds.pageFooterDI.toString() &&
          message.insertMethod === DragDropInsertMethod.insertAfter) ||
        (simpleTargetKuikaId === SpecialDIIds.layoutHeaderDI.toString() &&
          message.insertMethod === DragDropInsertMethod.insertAfter) ||
        (simpleTargetKuikaId === SpecialDIIds.layoutFooterDI.toString() &&
          message.insertMethod === DragDropInsertMethod.insertAfter) ||
        (simpleTargetKuikaId === SpecialDIIds.layoutLeftDI.toString() &&
          message.insertMethod === DragDropInsertMethod.insertAfter) ||
        (simpleTargetKuikaId === SpecialDIIds.layoutRightDI.toString() &&
          message.insertMethod === DragDropInsertMethod.insertAfter)
      ) {
        let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
          sd,
          this.screenContext.screenProvider.getScreenDesignDictionary(),
          targetDI.id,
          newComponent,
          this.screenContext.state.project.componentSetObj.components,
          message.fixedPositionInfo,
          message.absolutePositionInfo,
          message.sourceKuikaId
        );

        newDI = ScreenDesignHelper.addDefaultComponentStylings(
          newDI,
          newComponentDefinition,
          this.screenContext.defaultStyleProvider
        );

        ScreenDesignHelper.addChildForItemContainer(
          newComponent,
          newDI,
          sd,
          this.screenContext.screenProvider.getScreenDesignDictionary(),
          this.screenContext.state.project.componentSetObj.components,
          this.screenContext.projectProvider.getProject().projectType,
          this.screenContext.defaultStyleProvider
        );
        if (newDI?.styleObj?.position && sd.isAbsoluteDesign) {
          if (newDI.item.name !== "StaticTable" && newDI.item.name !== "ReportTable") {
            newDI.styleObj.position.width = newDI?.fixedPositionInfo?.width;
            newDI.styleObj.position.height = newDI?.fixedPositionInfo?.height;
          }
          newDI.styleObj.position.yAxis = newDI?.fixedPositionInfo?.top;
          newDI.styleObj.position.xAxis = newDI?.fixedPositionInfo?.left;
        }
        const isElementHeightHigherThanContainer = KMainFunctions.getElementHeightIsHigherThanContainer(
          message?.fixedPositionInfo,
          targetDI,
          sd,
          undefined,
          true
        );
        if (
          isElementHeightHigherThanContainer &&
          targetDI?.styleObj?.position?.height &&
          message?.fixedPositionInfo?.height &&
          targetDI?.item?.name !== "StaticTableColumn" &&
          parentDi?.item?.name !== "StaticTableColumn" &&
          targetDI?.item?.name !== "ReportTableColumn" &&
          targetDI?.item?.name !== "VerticalPanel" &&
          targetDI?.item?.name !== "Box"
        ) {
          targetDI.styleObj.position.height += message.fixedPositionInfo.height;
        }
        if (
          (targetDI?.item?.name === "ReportTableColumn" ||
            parentDi?.item?.name === "ReportTableColumn" ||
            parentDi?.item?.name === "StaticTableColumn") &&
          newDI?.styleObj?.position &&
          message?.fixedPositionInfo &&
          newDI.item.name !== "StaticTable" &&
          newDI.item.name !== "ReportTable"
        ) {
          newDI.styleObj.position.width = undefined;
          newDI.styleObj.position.height = message?.fixedPositionInfo?.height;
        }
        this.placeToLastChildItem(sd, targetDI, newDI);
      } else {
        let isDroppedToBeforeOrAfterRow = false;
        let isDroppedToBeforeOrAfterRowAndIntoCol = false;

        const targetComponent: IDisplayItemComponent | undefined = targetDI?.item;
        const targetComponentDefinition: IComponentDefinition | undefined =
          this.getComponentDefinition(targetComponent);

        if (
          targetComponentDefinition?.componentType === ComponentType.responsiveRow &&
          (message.insertMethod === DragDropInsertMethod.insertAfter ||
            message.insertMethod === DragDropInsertMethod.insertBefore)
        ) {
          isDroppedToBeforeOrAfterRow = true;
        } else if (
          targetComponent &&
          targetComponentDefinition &&
          targetComponentDefinition.componentType === ComponentType.responsiveColumn &&
          (message.insertMethod === DragDropInsertMethod.insertAfter ||
            message.insertMethod === DragDropInsertMethod.insertBefore) &&
          newComponentDefinition &&
          newComponentDefinition.componentType !== ComponentType.responsiveColumn
        ) {
          isDroppedToBeforeOrAfterRow = true;
        }
        let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
          sd,
          this.screenContext.screenProvider.getScreenDesignDictionary(),
          isDroppedToBeforeOrAfterRow ? undefined : this.getParentId(targetDI, sd.displayItems),
          newComponent,
          this.screenContext.state.project.componentSetObj.components,
          message.fixedPositionInfo,
          message.absolutePositionInfo,
          message.sourceKuikaId
        );
        newDI = ScreenDesignHelper.addDefaultComponentStylings(
          newDI,
          newComponentDefinition,
          this.screenContext.defaultStyleProvider
        );

        const isWidthDisabled = newComponentDefinition?.inVisibleStyles?.includes("layout_width");
        if (
          parentDi?.item.name === "HorizontalStack" &&
          newDI?.styleObj?.position &&
          !isWidthDisabled &&
          newComponentDefinition?.name !== "Checkbox" &&
          newComponentDefinition?.name !== "Switch"
        ) {
          newDI.styleObj.position.width = 100;
        }
        if (newDI?.item.name === "Panel" && newDI?.styleObj?.position && sd.isAbsoluteDesign === true) {
          newDI.styleObj.position.height = 100;
        }

        if (isDroppedToBeforeOrAfterRow && newComponentDefinition.componentType !== ComponentType.responsiveRow) {
          ScreenDesignHelper.addChildForItemContainer(
            newComponent,
            newDI,
            sd,
            this.screenContext.screenProvider.getScreenDesignDictionary(),
            this.screenContext.state.project.componentSetObj.components,
            this.screenContext.projectProvider.getProject().projectType,
            this.screenContext.defaultStyleProvider
          );
        }
        const newDIComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(newDI?.item);

        const nextSelectedKuikaId = newDI.id.toString();

        if (
          targetComponent &&
          targetComponentDefinition &&
          targetComponentDefinition.componentType === ComponentType.responsiveColumn &&
          (message.insertMethod === DragDropInsertMethod.insertAfter ||
            message.insertMethod === DragDropInsertMethod.insertBefore) &&
          newDIComponentDefinition &&
          newDIComponentDefinition.componentType !== ComponentType.responsiveColumn
        ) {
          isDroppedToBeforeOrAfterRowAndIntoCol = true;
        }

        if (
          isDroppedToBeforeOrAfterRow &&
          newComponentDefinition.componentType !== ComponentType.responsiveColumn &&
          newComponentDefinition.componentType !== ComponentType.responsiveRow
        ) {
          if (sd.isAbsoluteDesign === true) {
            newDI = this.wrapDIWithDefaultPanel(
              newDI,
              sd,
              false,
              message.fixedPositionInfo,
              message.absolutePositionInfo
            );
          } else {
            newDI = this.wrapDIWithDefaultRow(newDI, sd);
          }
        }
        if (
          isDroppedToBeforeOrAfterRow &&
          message.insertMethod === DragDropInsertMethod.insertAfter &&
          newDIComponentDefinition?.componentType !== ComponentType.responsiveRow &&
          !isDroppedToBeforeOrAfterRowAndIntoCol
        ) {
          this.placeAfterSpecificItem(sd, targetDI, newDI, nextSelectedKuikaId);
          newDI.parentId = this.getParentId(targetDI, sd.displayItems);
        } else if (
          isDroppedToBeforeOrAfterRow &&
          message.insertMethod === DragDropInsertMethod.insertBefore &&
          newDIComponentDefinition?.componentType !== ComponentType.responsiveRow &&
          !isDroppedToBeforeOrAfterRowAndIntoCol
        ) {
          this.placeToBeforeItem(sd, targetDI, newDI, nextSelectedKuikaId);
          newDI.parentId = this.getParentId(targetDI, sd.displayItems);
        }

        // if (
        //   targetComponent &&
        //   targetComponentDefinition &&
        //   targetComponentDefinition.componentType === ComponentType.responsiveColumn
        // ) {
        //   return;
        // }
        if (
          !(
            targetComponent &&
            targetComponentDefinition &&
            targetComponentDefinition.componentType === ComponentType.responsiveRow
          ) &&
          targetComponentDefinition?.componentType !== ComponentType.responsiveColumn
        ) {
          ScreenDesignHelper.addChildForItemContainer(
            newComponent,
            newDI,
            sd,
            this.screenContext.screenProvider.getScreenDesignDictionary(),
            this.screenContext.state.project.componentSetObj.components,
            this.screenContext.projectProvider.getProject().projectType,
            this.screenContext.defaultStyleProvider
          );
          if (message.insertMethod === DragDropInsertMethod.insertAfter) {
            if (
              targetDI?.item?.name === "TableRow" ||
              targetDI?.item?.name === "ReportTableRow" ||
              targetDI?.item?.name === "StaticTableRow"
            ) {
              const tableDI = sdDict[this.getParentId(targetDI, sd.displayItems) as number];

              if (tableDI) {
                newDI.parentId = this.getParentId(tableDI, sd.displayItems);
                if (sd.isAbsoluteDesign === true && newDIComponentDefinition?.componentType === ComponentType.simple) {
                  newDI = this.wrapDIWithDefaultPanel(
                    newDI,
                    sd,
                    false,
                    message.fixedPositionInfo,
                    message.absolutePositionInfo
                  );
                }
                this.placeAfterSpecificItem(sd, tableDI, newDI, nextSelectedKuikaId);
              }
            } else if (
              parentDi &&
              (targetComponentParentDefinition?.name === "Badge" || targetComponentParentDefinition?.name === "Tooltip")
            ) {
              newDI.parentId = this.getParentId(parentDi, sd.displayItems);
              this.placeAfterSpecificItem(sd, parentDi, newDI, nextSelectedKuikaId);
            } else if (parentDi && targetComponentParentDefinition?.name === "ContentMenuPanel") {
              const parentOfParentDI = sdDict[this.getParentId(parentDi, sd.displayItems) as number];
              if (parentOfParentDI) {
                newDI.parentId = this.getParentId(parentOfParentDI, sd.displayItems);
                this.placeAfterSpecificItem(sd, parentOfParentDI, newDI, nextSelectedKuikaId);
              }
            } else {
              if (
                (targetDI?.item?.name === "ReportTableColumn" ||
                  targetDI?.item?.name === "StaticTableColumn" ||
                  parentDi?.item?.name === "ReportTableColumn" ||
                  parentDi?.item?.name === "StaticTableColumn") &&
                newDI?.styleObj?.position &&
                message?.fixedPositionInfo &&
                newDI.item.name !== "StaticTable" &&
                newDI.item.name !== "ReportTable"
              ) {
                newDI.styleObj.position.width = undefined;
                newDI.styleObj.position.height = message?.fixedPositionInfo?.height;
              }
              this.placeAfterSpecificItem(sd, targetDI, newDI, nextSelectedKuikaId);
            }
          } else if (message.insertMethod === DragDropInsertMethod.insertBefore) {
            if (
              targetDI?.item?.name === "TableHeader" ||
              targetDI?.item?.name === "ReportTableHeader" ||
              targetDI?.item?.name === "StaticTableHeader"
            ) {
              const tableDI = sdDict[this.getParentId(targetDI, sd.displayItems) as number];

              if (tableDI) {
                newDI.parentId = this.getParentId(tableDI, sd.displayItems);
                if (sd.isAbsoluteDesign === true && newDIComponentDefinition?.componentType === ComponentType.simple) {
                  newDI = this.wrapDIWithDefaultPanel(
                    newDI,
                    sd,
                    false,
                    message.fixedPositionInfo,
                    message.absolutePositionInfo
                  );
                }
                this.placeToBeforeItem(sd, tableDI, newDI, nextSelectedKuikaId);
              }
            } else if (
              parentDi &&
              (targetComponentParentDefinition?.name === "Badge" || targetComponentParentDefinition?.name === "Tooltip")
            ) {
              newDI.parentId = this.getParentId(parentDi, sd.displayItems);
              this.placeToBeforeItem(sd, parentDi, newDI, nextSelectedKuikaId);
            } else if (parentDi && targetComponentParentDefinition?.name === "ContentMenuPanel") {
              const parentOfParentDI = sdDict[this.getParentId(parentDi, sd.displayItems) as number];

              if (parentOfParentDI) {
                newDI.parentId = this.getParentId(parentOfParentDI, sd.displayItems);
                this.placeToBeforeItem(sd, parentOfParentDI, newDI, nextSelectedKuikaId);
              }
            } else {
              if (
                (targetDI?.item?.name === "ReportTableColumn" ||
                  targetDI?.item?.name === "StaticTableColumn" ||
                  parentDi?.item?.name === "ReportTableColumn" ||
                  parentDi?.item?.name === "StaticTableColumn") &&
                newDI?.styleObj?.position &&
                message?.fixedPositionInfo &&
                newDI.item.name !== "StaticTable" &&
                newDI.item.name !== "ReportTable"
              ) {
                newDI.styleObj.position.width = undefined;
                newDI.styleObj.position.height = message?.fixedPositionInfo?.height;
              }
              this.placeToBeforeItem(sd, targetDI, newDI, nextSelectedKuikaId);
            }
          }
        } else if (isDroppedToBeforeOrAfterRowAndIntoCol) {
          const parentDI = sdDict[this.getParentId(targetDI, sd.displayItems) as number];

          if (parentDI) {
            ScreenDesignHelper.addChildForItemContainer(
              newComponent,
              newDI,
              sd,
              this.screenContext.screenProvider.getScreenDesignDictionary(),
              this.screenContext.state.project.componentSetObj.components,
              this.screenContext.projectProvider.getProject().projectType,
              this.screenContext.defaultStyleProvider
            );

            newDI.parentId = this.getParentId(parentDI, sd.displayItems);

            if (message.insertMethod === DragDropInsertMethod.insertAfter) {
              this.placeAfterSpecificItem(sd, parentDI, newDI, nextSelectedKuikaId);
            } else if (message.insertMethod === DragDropInsertMethod.insertBefore) {
              this.placeToBeforeItem(sd, parentDI, newDI, nextSelectedKuikaId);
            }
          }
        } else if (
          targetComponentDefinition.componentType === ComponentType.responsiveRow &&
          newDIComponentDefinition &&
          newDIComponentDefinition.componentType === ComponentType.responsiveRow
        ) {
          ScreenDesignHelper.addChildForItemContainer(
            newComponent,
            newDI,
            sd,
            this.screenContext.screenProvider.getScreenDesignDictionary(),
            this.screenContext.state.project.componentSetObj.components,
            this.screenContext.projectProvider.getProject().projectType,
            this.screenContext.defaultStyleProvider
          );

          if (message.insertMethod === DragDropInsertMethod.insertAfter) {
            this.placeAfterSpecificItem(sd, targetDI, newDI, nextSelectedKuikaId);
          } else {
            this.placeToBeforeItem(sd, targetDI, newDI, nextSelectedKuikaId);
          }

          newDI.parentId = this.getParentId(targetDI, sd.displayItems);
        } else if (
          message.insertMethod !== DragDropInsertMethod.insertAfter &&
          message.insertMethod !== DragDropInsertMethod.insertBefore
        )
          KMainFunctions.displayWarningNotification("You can not drop item in a row.");
      }
    }
  };

  dragDropByMoveItemMessage = (message: IDragDropByMoveItemMessage) => {
    const sd = this.screenContext.screenProvider.getScreenDesign();
    const sdDict = this.screenContext.screenProvider.getScreenDesignDictionary();
    let simpleTargetKuikaId = message.targetKuikaId.replace("-lastChildItem", "");
    const simpleSourceKuikaId = message.sourceKuikaId.replace("-lastChildItem", "");

    if (!sd || simpleTargetKuikaId === message.sourceKuikaId) {
      return;
    }

    const sourceParentData = CommonHelperFunctions.getParentDataForDraggableComponent(
      message.sourceKuikaId,
      sd.displayItems,
      this.screenContext.state.project
    );
    const isSourceHasParentData = !!sourceParentData?.currentParentComponentName;
    let targetDI = sdDict[parseInt(simpleTargetKuikaId, 10)];

    if (!targetDI) {
      return;
    }

    const sourceDI = message.sourceDI ?? sdDict[parseInt(simpleSourceKuikaId, 10)];
    const sourceComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(sourceDI?.item);

    if (
      targetDI.parentId &&
      targetDI?.item?.name === "Col" &&
      sourceComponentDefinition &&
      sourceComponentDefinition.componentType !== ComponentType.responsiveColumn &&
      (message?.insertMethod === DragDropInsertMethod.insertAfter ||
        message?.insertMethod === DragDropInsertMethod.insertBefore)
    ) {
      const parentId = this.getParentId(targetDI, sd.displayItems);

      if (parentId) {
        targetDI = sdDict[parentId];
        simpleTargetKuikaId = targetDI?.id.toString() || "";
      }
    }

    // KOA-18016 (move mirror of the create path): a before/after move of a cell element onto a
    // StaticTable/ReportTable column would re-parent the moved element to the column's parent
    // (StaticTableRow), orphaning it outside any column and corrupting the table. Redirect the move INTO
    // the column instead. Excluded: moving a column onto a sibling column (a legitimate column REORDER),
    // which must keep its before/after semantics rather than becoming a column-in-column drop.
    if (
      targetDI &&
      (targetDI.item?.name === "StaticTableColumn" || targetDI.item?.name === "ReportTableColumn") &&
      sourceDI?.item?.name !== "StaticTableColumn" &&
      sourceDI?.item?.name !== "ReportTableColumn" &&
      (message?.insertMethod === DragDropInsertMethod.insertAfter ||
        message?.insertMethod === DragDropInsertMethod.insertBefore)
    ) {
      message.insertMethod = DragDropInsertMethod.insertInto;
    }

    if (!targetDI) {
      return;
    }

    const targetComponent: IDisplayItemComponent | undefined = targetDI?.item;
    const targetComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(targetComponent);
    const parentDI = sdDict[this.getParentId(targetDI, sd.displayItems) as number];

    let targetParentComponent: IDisplayItemComponent | undefined;
    if (parentDI) {
      targetParentComponent = parentDI.item;
    }
    let targetComponentParentDefinition: IComponentDefinition | undefined;
    if (targetParentComponent) {
      targetComponentParentDefinition = this.getComponentDefinition(targetParentComponent);
    }

    // In report designer, if dragged component is simple and target is simple, use insertAfter instead of insertInto
    if (
      sd.isAbsoluteDesign === true &&
      message.insertMethod === DragDropInsertMethod.insertInto &&
      sourceComponentDefinition?.componentType === ComponentType.simple &&
      targetComponentDefinition?.componentType === ComponentType.simple
    ) {
      message.insertMethod = DragDropInsertMethod.insertAfter;
    }

    if (isSourceHasParentData) {
      let targetDI = sdDict[simpleTargetKuikaId];

      const sourceDI = message.sourceDI ?? sdDict[simpleSourceKuikaId];
      let sourceComponentName = "sourceComponentName";
      if (sourceDI) {
        sourceComponentName = CommonHelperFunctions.getComponentName(
          sourceDI,
          this.screenContext.state.project.componentSetObj.components
        );
      }
      let targetComponentName = "targetComponentName";
      if (targetDI) {
        targetComponentName = CommonHelperFunctions.getComponentName(
          targetDI,
          this.screenContext.state.project.componentSetObj.components
        );
      }

      let targetParentComponentName = "targetParentComponentName";
      if (targetDI?.parentId) {
        const targetParentDI = sdDict[this.getParentId(targetDI, sd.displayItems) as number];

        if (targetParentDI) {
          targetParentComponentName = CommonHelperFunctions.getComponentName(
            targetParentDI,
            this.screenContext.state.project.componentSetObj.components
          );
        }
      }

      if (
        !CommonHelperFunctions.canIDropAChildItem(
          sourceComponentName,
          targetComponentName,
          targetParentComponentName,
          sourceParentData
        )
      ) {
        KMainFunctions.displayWarningNotification("Can't drop child item here!");
        return;
      }
    }
    const isElementDraggable = message.sourceDI
      ? true
      : this.getElementIsDroppable(
          targetComponent,
          targetComponentDefinition,
          sourceComponentDefinition,
          targetDI,
          sd,
          sdDict,
          message,
          sourceDI,
          simpleTargetKuikaId,
          simpleSourceKuikaId
        );

    if (!isElementDraggable) {
      return;
    }

    let newDisplayItem: IDisplayItem | undefined =
      message.sourceDI ?? this.cloneAndRemoveDisplayItemByKuikaid(message.sourceKuikaId, sd);

    if (newDisplayItem && message.fixedPositionInfo) {
      newDisplayItem.fixedPositionInfo = message.fixedPositionInfo;
      newDisplayItem.absolutePositionInfo = message.absolutePositionInfo;
    }
    if (
      newDisplayItem?.styleObj?.position &&
      message.fixedPositionInfo &&
      sd.isAbsoluteDesign === true &&
      newDisplayItem.item.name !== "Panel"
    ) {
      if (newDisplayItem.item.name !== "StaticTable" && newDisplayItem.item.name !== "ReportTable") {
        newDisplayItem.styleObj.position.width = message?.fixedPositionInfo?.width;
        newDisplayItem.styleObj.position.height = message?.fixedPositionInfo?.height;
      }
      newDisplayItem.styleObj.position.yAxis = message?.fixedPositionInfo?.top;
      newDisplayItem.styleObj.position.xAxis = message?.fixedPositionInfo?.left;

      if (
        (targetDI?.item?.name === "ReportTableColumn" ||
          targetDI?.item?.name === "StaticTableColumn" ||
          parentDI?.item?.name === "ReportTableColumn" ||
          parentDI?.item?.name === "StaticTableColumn") &&
        newDisplayItem.item.name !== "StaticTable" &&
        newDisplayItem.item.name !== "ReportTable"
      ) {
        newDisplayItem.styleObj.position.width = undefined;
      }
    }
    let isElementCanNotDraggableOnReportDesigner = false;
    if (!newDisplayItem) {
      return;
    }

    if (
      message.insertMethod === DragDropInsertMethod.insertTopOfRootDI ||
      message.insertMethod === DragDropInsertMethod.insertBottomOfRootDI
    ) {
      let newDI = newDisplayItem as IDisplayItem;
      const newCompDef = this.getComponentDefinition(newDI?.item) as IComponent;
      const nextSelectedKuikaId =
        message.nextSelectedDiId !== undefined ? message.nextSelectedDiId : newDI.id.toString();
      if (
        newCompDef.componentType !== ComponentType.responsiveColumn &&
        newCompDef.componentType !== ComponentType.responsiveRow
      ) {
        if (sd.isAbsoluteDesign === true) {
          newDI = this.wrapDIWithDefaultPanel(
            newDI,
            sd,
            false,
            message.fixedPositionInfo,
            message.absolutePositionInfo
          );
        } else {
          newDI = this.wrapDIWithDefaultRow(newDI, sd);
        }
      }
      if (message.insertMethod === DragDropInsertMethod.insertTopOfRootDI) {
        this.placeToRootFirstItem(sd, newDI, nextSelectedKuikaId);
      } else {
        this.placeToRootLastItem(sd, newDI, nextSelectedKuikaId);
      }
    } else if (message.targetKuikaId === "RootLayout" || message.targetKuikaId === "-1") {
      let newDI = newDisplayItem as IDisplayItem;
      const newCompDef = this.getComponentDefinition(newDI?.item) as IComponent;
      const nextSelectedKuikaId =
        message.nextSelectedDiId !== undefined ? message.nextSelectedDiId : newDI.id.toString();
      if (
        newCompDef.componentType !== ComponentType.responsiveColumn &&
        newCompDef.componentType !== ComponentType.responsiveRow
      ) {
        if (sd.isAbsoluteDesign === true) {
          newDI = this.wrapDIWithDefaultPanel(
            newDI,
            sd,
            false,
            message.fixedPositionInfo,
            message.absolutePositionInfo
          );
        } else {
          newDI = this.wrapDIWithDefaultRow(newDI, sd);
        }
      } else if (newCompDef.componentType === ComponentType.responsiveColumn) {
        newDI = this.wrapDIWithDefaultRow(newDI, sd, false);
      }
      this.removePanelDiIfEmpty(sd, sourceDI, targetDI);
      this.placeToRootLastItem(sd, newDI, nextSelectedKuikaId);
    } else {
      const targetDI = sdDict[parseInt(simpleTargetKuikaId, 10)];

      if (targetDI) {
        // check if the DI is Column, and the target DI is column
        const target: IDisplayItemComponent | undefined = targetDI?.item;
        const targetDefinition = this.getComponentDefinition(target);
        const newDI: IDisplayItemComponent | undefined = newDisplayItem?.item;
        const newDIDefinition = this.getComponentDefinition(newDI);
        const nextSelectedKuikaId =
          message.nextSelectedDiId !== undefined ? message.nextSelectedDiId : newDisplayItem.id.toString();

        const properties = newDI?.properties;
        const isParentHeaderOrFooter = ScreenDesignHelper.isParentHeaderOrFooter(targetDI, sd.displayItems);
        const isParentLeftOrRightLayout = ScreenDesignHelper.isParentLeftOrRightLayout(targetDI, sd.displayItems);
        if (newDI?.name === "Menu") {
          let mode = "vertical";
          if (isParentHeaderOrFooter) {
            mode = "horizontal";
          } else if (isParentLeftOrRightLayout) {
            mode = "vertical";
          }
          properties.forEach((p) => {
            if (p.name === "mode") {
              p.symbol.fixedValue = mode;
            }
          });
        }
        if (
          target &&
          targetDefinition &&
          targetDefinition.componentType === ComponentType.responsiveColumn &&
          newDI &&
          newDIDefinition &&
          newDIDefinition.componentType === ComponentType.responsiveColumn
        ) {
          newDisplayItem.parentId = this.getParentId(targetDI, sd.displayItems);
          const sourceDIParentId = this.getParentId(sourceDI, sd.displayItems);

          if (message.insertMethod === DragDropInsertMethod.insertAfter) {
            this.placeAfterSpecificItem(sd, targetDI, newDisplayItem, nextSelectedKuikaId, sourceDIParentId);
          } else {
            this.placeToBeforeItem(sd, targetDI, newDisplayItem, nextSelectedKuikaId, sourceDIParentId);
          }

          return;
        }
        // check if the DI is Column and the target DI is a row
        if (
          target &&
          targetDefinition &&
          targetDefinition.componentType === ComponentType.responsiveRow &&
          newDI &&
          newDIDefinition &&
          newDIDefinition.componentType === ComponentType.responsiveColumn
        ) {
          if (target !== undefined) {
            newDisplayItem.parentId = targetDI.id;
            this.placeToLastChildItem(sd, targetDI, newDisplayItem);
            return;
          }
        }
        if (target && targetDefinition && targetDefinition.componentType === ComponentType.container) {
          const nextSelectedKuikaId =
            message.nextSelectedDiId !== undefined ? message.nextSelectedDiId : newDisplayItem.id.toString();
          if (
            sourceComponentDefinition &&
            sourceComponentDefinition.componentType === ComponentType.simple &&
            sourceComponentDefinition.name !== "Table" &&
            sourceComponentDefinition.name !== "ReportTable" &&
            sourceComponentDefinition.name !== "StaticTable"
          ) {
            message.insertMethod = DragDropInsertMethod.insertInto;
          }
          if (message.insertMethod === DragDropInsertMethod.insertInto) {
            if (target !== undefined) {
              const isHeightExpandableTarget =
                targetDI?.item?.name === "Header" ||
                targetDI?.item?.name === "Footer" ||
                targetDI?.item?.name === "Page Header" ||
                targetDI?.item?.name === "Page Footer" ||
                targetDI?.item?.name === "Box" ||
                targetDI?.item?.name === "Panel";
              if (isHeightExpandableTarget) {
                const isElementHeightHigherThanContainer = KMainFunctions.getElementHeightIsHigherThanContainer(
                  message?.fixedPositionInfo,
                  targetDI,
                  sd
                );
                if (isElementCanNotDraggableOnReportDesigner === false) {
                  if (
                    isElementHeightHigherThanContainer &&
                    targetDI?.styleObj?.position?.height &&
                    message?.fixedPositionInfo?.height
                  ) {
                    targetDI.styleObj.position.height += message.fixedPositionInfo.height;
                  }
                }
              }
              if (isElementCanNotDraggableOnReportDesigner === false) {
                this.removePanelDiIfEmpty(sd, sourceDI, targetDI);
                newDisplayItem.parentId = targetDI.id;
                this.placeToLastChildItem(sd, targetDI, newDisplayItem);
              } else {
                const target = sdDict[this.getParentId(message.oldDI!, sd.displayItems) as number];
                this.placeAfterSpecificItem(
                  sd,
                  target,
                  message.oldDI as any,
                  nextSelectedKuikaId,
                  undefined,
                  isElementCanNotDraggableOnReportDesigner
                );
              }
            }
          } else if (message.insertMethod === DragDropInsertMethod.insertAfter) {
            newDisplayItem.parentId = this.getParentId(targetDI, sd.displayItems);
            if (sd.isAbsoluteDesign === true && newDIDefinition?.componentType === ComponentType.simple) {
              newDisplayItem = this.wrapDIWithDefaultPanel(
                newDisplayItem,
                sd,
                false,
                message.fixedPositionInfo,
                message.absolutePositionInfo,
                true
              );
            }
            if (parentDI && targetComponentParentDefinition?.name === "ContentMenu") {
              newDisplayItem.parentId = this.getParentId(parentDI, sd.displayItems);
              this.placeAfterSpecificItem(sd, parentDI, newDisplayItem, nextSelectedKuikaId);
            } else {
              this.placeAfterSpecificItem(sd, targetDI, newDisplayItem, nextSelectedKuikaId);
            }
          } else if (message.insertMethod === DragDropInsertMethod.insertBefore) {
            newDisplayItem.parentId = this.getParentId(targetDI, sd.displayItems);
            if (sd.isAbsoluteDesign === true && newDIDefinition?.componentType === ComponentType.simple) {
              newDisplayItem = this.wrapDIWithDefaultPanel(
                newDisplayItem,
                sd,
                false,
                message.fixedPositionInfo,
                message.absolutePositionInfo,
                true
              );
            }
            if (parentDI?.item.name === "ContentMenu") {
              newDisplayItem.parentId = this.getParentId(parentDI, sd.displayItems);
              this.placeToBeforeItem(sd, parentDI, newDisplayItem, nextSelectedKuikaId);
            } else {
              this.placeToBeforeItem(sd, targetDI, newDisplayItem, nextSelectedKuikaId);
            }
          }
        } else if (
          message.targetKuikaId.includes("-lastChildItem") ||
          message.insertMethod === DragDropInsertMethod.insertInto
        ) {
          if (target !== undefined) {
            const isHeightExpandableTarget =
              targetDI?.item?.name === "Header" ||
              targetDI?.item?.name === "Footer" ||
              targetDI?.item?.name === "Page Header" ||
              targetDI?.item?.name === "Page Footer" ||
              targetDI?.item?.name === "Box" ||
              targetDI?.item?.name === "Panel";
            if (isHeightExpandableTarget) {
              const isElementHeightHigherThanContainer = KMainFunctions.getElementHeightIsHigherThanContainer(
                message?.fixedPositionInfo,
                targetDI,
                sd,
                message?.absolutePositionInfo
              );
              if (
                isElementHeightHigherThanContainer &&
                targetDI?.styleObj?.position?.height &&
                message?.fixedPositionInfo?.height
              ) {
                targetDI.styleObj.position.height += message.fixedPositionInfo.height;
              }
            }
            newDisplayItem.parentId = this.getParentId(targetDI, sd.displayItems);
            const isTargetDIFooter =
              CommonHelperFunctions.isTargetDIFooterByPosition(sd, message.absolutePositionInfo?.top) ||
              message.targetKuikaId === "-3";
            const isTargetDIHeader =
              CommonHelperFunctions.isTargetDIHeaderByPosition(sd, message.absolutePositionInfo?.top) ||
              message.targetKuikaId === "-2";
            if (
              message.insertMethod === DragDropInsertMethod.insertBefore &&
              (targetComponent?.name === "TableColumn" ||
                targetComponent?.name === "ReportTableColumn" ||
                targetComponent?.name === "EditableTableColumn" ||
                targetComponent?.name === "TableTemplateColumn" ||
                targetComponent?.name === "TableDefaultColumn" ||
                targetComponent?.name === "StaticTableColumn") &&
              (newDI.name === "TableColumn" ||
                newDI.name === "ReportTableColumn" ||
                newDI.name === "EditableTableColumn" ||
                newDI.name === "TableDefaultColumn" ||
                newDI.name === "TableTemplateColumn" ||
                newDI.name === "StaticTableColumn")
            ) {
              this.placeToBeforeItem(sd, targetDI, newDisplayItem, nextSelectedKuikaId);
            } else if (
              message.insertMethod === DragDropInsertMethod.insertAfter &&
              (targetComponent?.name === "TableColumn" ||
                targetComponent?.name === "ReportTableColumn" ||
                targetComponent?.name === "EditableTableColumn" ||
                targetComponent?.name === "TableTemplateColumn" ||
                targetComponent?.name === "TableDefaultColumn" ||
                targetComponent?.name === "StaticTableColumn") &&
              (newDI.name === "TableColumn" ||
                newDI.name === "ReportTableColumn" ||
                newDI.name === "EditableTableColumn" ||
                newDI.name === "TableTemplateColumn" ||
                newDI.name === "TableDefaultColumn" ||
                newDI.name === "StaticTableColumn")
            ) {
              this.placeAfterSpecificItem(sd, targetDI, newDisplayItem, nextSelectedKuikaId);
            } else if (message.insertMethod === DragDropInsertMethod.insertAfter) {
              if (
                (simpleTargetKuikaId === SpecialDIIds.headerDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.footerDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.pageHeaderDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.pageFooterDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.layoutHeaderDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.layoutFooterDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.layoutLeftDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.layoutRightDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter)
              ) {
                newDisplayItem.parentId = targetDI.id;
                this.placeToLastChildItem(sd, targetDI, newDisplayItem);
              } else {
                newDisplayItem.parentId = targetDI.id;
                this.placeToLastChildItem(sd, targetDI, newDisplayItem);
              }
            } else if (
              sd.isAbsoluteDesign === true &&
              newDIDefinition?.componentType === ComponentType.simple &&
              ((!isTargetDIFooter && message.targetKuikaId === SpecialDIIds.footerDI.toString()) ||
                (!isTargetDIHeader && message.targetKuikaId === SpecialDIIds.headerDI.toString()))
            ) {
              const panelDI = CommonHelperFunctions.findPanelDIByPosition(sd, message.absolutePositionInfo?.top);
              if (panelDI) {
                newDisplayItem.parentId = panelDI.id;
                this.placeToLastChildItem(sd, panelDI, newDisplayItem);
              } else {
                newDisplayItem = this.wrapDIWithDefaultPanel(
                  newDisplayItem,
                  sd,
                  false,
                  message.fixedPositionInfo,
                  message.absolutePositionInfo,
                  true
                );
                const lastDI = CommonHelperFunctions.findLastDI(sd);
                this.placeAfterSpecificItem(sd, lastDI ?? targetDI, newDisplayItem, nextSelectedKuikaId);
              }
            } else {
              newDisplayItem.parentId = targetDI.id;
              this.placeToLastChildItem(sd, targetDI, newDisplayItem);
            }
          }
        } else {
          const targetComponent: IDisplayItemComponent | undefined = targetDI?.item;
          const targetComponentDefinition = this.getComponentDefinition(targetComponent);
          if (
            targetComponent &&
            targetComponentDefinition &&
            targetComponentDefinition.componentType === ComponentType.responsiveRow
          ) {
            if (newDIDefinition?.componentType === ComponentType.responsiveRow) {
              newDisplayItem.parentId = this.getParentId(targetDI, sd.displayItems);
              const nextSelectedKuikaId =
                message.nextSelectedDiId !== undefined ? message.nextSelectedDiId : newDisplayItem.id.toString();
              if (message.insertMethod === DragDropInsertMethod.insertAfter) {
                this.placeAfterSpecificItem(sd, targetDI, newDisplayItem, nextSelectedKuikaId);
              } else {
                this.placeToBeforeItem(sd, targetDI, newDisplayItem, nextSelectedKuikaId);
              }
            } else if (message.insertMethod === DragDropInsertMethod.insertAfter) {
              let newDI = newDisplayItem as IDisplayItem;
              newDI = this.wrapDIWithDefaultRow(newDI, sd);
              newDI.parentId = this.getParentId(targetDI, sd.displayItems);
              this.placeAfterSpecificItem(sd, targetDI, newDI, nextSelectedKuikaId);
            } else {
              let newDI = newDisplayItem as IDisplayItem;
              newDI = this.wrapDIWithDefaultRow(newDI, sd);
              newDI.parentId = this.getParentId(targetDI, sd.displayItems);
              this.placeToBeforeItem(sd, targetDI, newDI, nextSelectedKuikaId);
            }
          } else if (
            targetComponent &&
            targetComponentDefinition &&
            targetComponentDefinition.componentType === ComponentType.responsiveColumn &&
            (message.insertMethod === DragDropInsertMethod.insertAfter ||
              message.insertMethod === DragDropInsertMethod.insertBefore) &&
            newDIDefinition &&
            newDIDefinition.componentType !== ComponentType.responsiveColumn
          ) {
            const parentDI = sdDict[this.getParentId(targetDI, sd.displayItems) as number];

            if (parentDI) {
              if (newDIDefinition?.componentType === ComponentType.responsiveRow) {
                newDisplayItem.parentId = this.getParentId(parentDI, sd.displayItems);
                const nextSelectedKuikaId =
                  message.nextSelectedDiId !== undefined ? message.nextSelectedDiId : newDisplayItem.id.toString();
                if (message.insertMethod === DragDropInsertMethod.insertAfter) {
                  this.placeAfterSpecificItem(sd, parentDI, newDisplayItem, nextSelectedKuikaId);
                } else {
                  this.placeToBeforeItem(sd, parentDI, newDisplayItem, nextSelectedKuikaId);
                }
              } else if (message.insertMethod === DragDropInsertMethod.insertAfter) {
                let newDI = newDisplayItem as IDisplayItem;
                newDI = this.wrapDIWithDefaultRow(newDI, sd);
                newDI.parentId = this.getParentId(parentDI, sd.displayItems);
                this.placeAfterSpecificItem(sd, parentDI, newDI, nextSelectedKuikaId);
              } else {
                let newDI = newDisplayItem as IDisplayItem;
                newDI = this.wrapDIWithDefaultRow(newDI, sd);
                newDI.parentId = this.getParentId(parentDI, sd.displayItems);
                this.placeToBeforeItem(sd, parentDI, newDI, nextSelectedKuikaId);
              }
            }
          } else {
            if (isElementCanNotDraggableOnReportDesigner === false) {
              newDisplayItem.parentId = this.getParentId(targetDI, sd.displayItems);
            }

            const nextSelectedKuikaId =
              message.nextSelectedDiId !== undefined ? message.nextSelectedDiId : newDisplayItem.id.toString();
            if (message.insertMethod === DragDropInsertMethod.insertAfter) {
              if (
                targetDI?.item?.name === "TableRow" ||
                targetDI?.item?.name === "ReportTableRow" ||
                targetDI?.item?.name === "StaticTableRow"
              ) {
                const tableDI = sdDict[this.getParentId(targetDI, sd.displayItems) as number];

                if (tableDI) {
                  newDisplayItem.parentId = this.getParentId(tableDI, sd.displayItems);
                  if (sd.isAbsoluteDesign === true && newDIDefinition?.componentType === ComponentType.simple) {
                    newDisplayItem = this.wrapDIWithDefaultPanel(
                      newDisplayItem,
                      sd,
                      false,
                      message.fixedPositionInfo,
                      message.absolutePositionInfo
                    );
                  }
                  this.placeAfterSpecificItem(sd, tableDI, newDisplayItem, nextSelectedKuikaId);
                }
              } else if (
                (simpleTargetKuikaId === SpecialDIIds.headerDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.footerDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.pageHeaderDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.pageFooterDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.layoutHeaderDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.layoutFooterDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.layoutLeftDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter) ||
                (simpleTargetKuikaId === SpecialDIIds.layoutRightDI.toString() &&
                  message.insertMethod === DragDropInsertMethod.insertAfter)
              ) {
                newDisplayItem.parentId = targetDI.id;
                this.placeToLastChildItem(sd, targetDI, newDisplayItem);
              } else if (
                parentDI &&
                (targetComponentParentDefinition?.name === "Badge" ||
                  targetComponentParentDefinition?.name === "Tooltip")
              ) {
                newDisplayItem.parentId = this.getParentId(parentDI, sd.displayItems);
                this.placeAfterSpecificItem(sd, parentDI, newDisplayItem, nextSelectedKuikaId);
              } else if (parentDI && targetComponentParentDefinition?.name === "ContentMenuPanel") {
                const parentOfParentDI = sdDict[this.getParentId(parentDI, sd.displayItems) as number];
                if (parentOfParentDI) {
                  newDisplayItem.parentId = this.getParentId(parentOfParentDI, sd.displayItems);
                  this.placeAfterSpecificItem(sd, parentOfParentDI, newDisplayItem, nextSelectedKuikaId);
                } else {
                  this.placeAfterSpecificItem(
                    sd,
                    targetDI,
                    newDisplayItem,
                    nextSelectedKuikaId,
                    undefined,
                    isElementCanNotDraggableOnReportDesigner
                  );
                }
              } else {
                newDisplayItem.parentId = this.getParentId(targetDI, sd.displayItems);
                this.placeAfterSpecificItem(
                  sd,
                  targetDI,
                  newDisplayItem,
                  nextSelectedKuikaId,
                  undefined,
                  isElementCanNotDraggableOnReportDesigner
                );
              }
            } else if (
              targetDI?.item?.name === "TableHeader" ||
              targetDI?.item?.name === "ReportTableHeader" ||
              targetDI?.item?.name === "StaticTableHeader"
            ) {
              const tableDI = sdDict[this.getParentId(targetDI, sd.displayItems) as number];

              if (tableDI) {
                newDisplayItem.parentId = this.getParentId(tableDI, sd.displayItems);
                if (sd.isAbsoluteDesign === true && newDIDefinition?.componentType === ComponentType.simple) {
                  newDisplayItem = this.wrapDIWithDefaultPanel(
                    newDisplayItem,
                    sd,
                    false,
                    message.fixedPositionInfo,
                    message.absolutePositionInfo
                  );
                }
                this.placeToBeforeItem(sd, tableDI, newDisplayItem, nextSelectedKuikaId);
              }
            } else if (
              parentDI &&
              (targetComponentParentDefinition?.name === "Badge" || targetComponentParentDefinition?.name === "Tooltip")
            ) {
              newDisplayItem.parentId = this.getParentId(parentDI, sd.displayItems);
              this.placeToBeforeItem(sd, parentDI, newDisplayItem, nextSelectedKuikaId);
            } else if (parentDI && targetComponentParentDefinition?.name === "ContentMenuPanel") {
              const parentOfParentDI = sdDict[this.getParentId(parentDI, sd.displayItems) as number];
              if (parentOfParentDI) {
                newDisplayItem.parentId = this.getParentId(parentOfParentDI, sd.displayItems);
                this.placeToBeforeItem(sd, parentOfParentDI, newDisplayItem, nextSelectedKuikaId);
              }
            } else {
              this.placeToBeforeItem(
                sd,
                targetDI,
                newDisplayItem,
                nextSelectedKuikaId,
                undefined,
                isElementCanNotDraggableOnReportDesigner
              );
            }
          }
        }
      }
    }
  };

  getIsNestedDataRepeater = (di: IDisplayItem): boolean => {
    const dataRepeaterParent = this.getDirectDataRepeaterParent(di);

    if (!dataRepeaterParent) {
      return false;
    }

    return this.getDirectDataRepeaterParent(dataRepeaterParent) !== undefined;
  };

  getDirectDataRepeaterParent = (di: IDisplayItem): IDisplayItem | undefined => {
    const sd = this.screenContext.screenProvider.getScreenDesign();
    const sdDict = this.screenContext.screenProvider.getScreenDesignDictionary();

    if (!sdDict || !di.parentId) {
      return undefined;
    }

    const parentDi = sdDict[this.getParentId(di, sd.displayItems) as number];

    if (!parentDi) {
      return undefined;
    }

    const componentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(parentDi?.item);

    if (
      componentDefinition?.name !== "TableTemplateColumn" &&
      componentDefinition?.name !== "TableColumn" &&
      componentDefinition?.name !== "EditableTableColumn" &&
      componentDefinition?.name !== "TableDefaultColumn" &&
      componentDefinition?.name !== "TableRow" &&
      componentDefinition?.isDataRepeater
    ) {
      return parentDi;
    }

    return this.getDirectDataRepeaterParent(parentDi);
  };

  getElementIsDroppable = (
    targetComponent: IDisplayItemComponent | undefined,
    targetComponentDefinition?: IComponentDefinition | undefined,
    sourceComponentDefinition?: IComponentDefinition,
    targetDI?: IDisplayItem,
    sd?: IScreenDesign,
    sdDict?: IScreenDesignDictionary,
    message?: IDragDropByMoveItemMessage | IDragDropByCreateItemMessage,
    sourceDI?: IDisplayItem,
    simpleTargetKuikaId?: string,
    simpleSourceKuikaId?: string,
    doNotTriggerNotifications?: boolean,
    isCreate?: boolean
  ): boolean => {
    if (!sd || !sdDict) {
      return false;
    }

    let parentComponent: IDisplayItemComponent | undefined;

    if (targetDI) {
      parentComponent = sdDict[this.getParentId(targetDI, sd?.displayItems) as number]?.item;
    }

    if (sourceComponentDefinition?.name === "MicroApp" && isCreate) {
      const isMicroAppExist = ScreenDesignHelper.isMicroAppExist(sd?.displayItems);
      if (isMicroAppExist) {
        if (doNotTriggerNotifications !== true)
          KMainFunctions.displayWarningNotification("You can not drop more than one micro app.");
        return false;
      }
    }

    // The agent chat renders as a single floating FAB — a second one on the same
    // screen would just overlap and open the same chat. Single-instance, like MicroApp.
    if (sourceComponentDefinition?.name === "AgentChat" && isCreate) {
      const agentChatExists = ScreenDesignHelper.componentExistsByName(sd?.displayItems, "AgentChat");
      if (agentChatExists) {
        if (doNotTriggerNotifications !== true)
          KMainFunctions.displayWarningNotification("You can not drop more than one agent chat.");
        return false;
      }
    }

    if (sourceDI && ScreenDesignHelper.getIsChildOfForm(sourceDI.id, sdDict)) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("Elements inside form can not be moved.");
      return false;
    }

    if (targetDI && ScreenDesignHelper.getIsChildOfForm(targetDI.id, sdDict)) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drop item into form.");
      return false;
    }

    if (
      sourceDI &&
      (sourceDI.isChildrenInteractionDisabled ||
        ScreenDesignHelper.getIsParentsChildrenInteractionDisabled(sourceDI.id, sdDict))
    ) {
      if (doNotTriggerNotifications !== true) {
        KMainFunctions.displayWarningNotification(`Elements inside ${sourceDI.friendlyName} can not be moved.`);
      }

      return false;
    }

    if (
      targetDI &&
      (targetDI.isChildrenInteractionDisabled ||
        ScreenDesignHelper.getIsParentsChildrenInteractionDisabled(targetDI.id, sdDict))
    ) {
      if (doNotTriggerNotifications !== true) {
        KMainFunctions.displayWarningNotification(`You can not drop item into ${targetDI.friendlyName}.`);
      }

      return false;
    }

    if (
      targetComponentDefinition?.name === "TableRow" &&
      ![
        "TableColumn",
        "ReportTableColumn",
        "EditableTableColumn",
        "TableTemplateColumn",
        "TableDefaultColumn",
        "StaticTableColumn"
      ].includes(sourceComponentDefinition?.name ?? "")
    ) {
      if (doNotTriggerNotifications !== true) {
        KMainFunctions.displayWarningNotification("You can not drop this item into table row.");
      }
      return false;
    }

    if (
      targetComponent &&
      (targetComponent.name === "Spacer" ||
        targetComponent.name === "HorizontalStack" ||
        targetComponent.name === "VerticalStack") &&
      (sourceComponentDefinition?.name === "Row" || sourceComponentDefinition?.name === "Col") &&
      message?.insertMethod === DragDropInsertMethod.insertInto
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drop this item into stacks.");
      return false;
    }

    if (
      (parentComponent?.name === "HorizontalStack" || parentComponent?.name === "VerticalStack") &&
      (message?.insertMethod === DragDropInsertMethod.insertAfter ||
        message?.insertMethod === DragDropInsertMethod.insertBefore) &&
      (sourceComponentDefinition?.name === "Row" || sourceComponentDefinition?.name === "Col")
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drop this item on stacks.");
      return false;
    }

    if (
      targetComponent &&
      // A Menu owns only MenuItem/SubMenu children (mirrors the MCP-path Rule 7
      // carve-out in component-drop-validation.ts, KOA-17797): allow dragging a
      // MenuItem/SubMenu INTO a Menu, reject anything else.
      ((targetComponent.name === "Menu" &&
        message?.insertMethod === DragDropInsertMethod.insertInto &&
        sourceComponentDefinition?.name !== "MenuItem" &&
        sourceComponentDefinition?.name !== "SubMenu") ||
        ((targetComponent.name === "MenuItem" || targetComponent.name === "SubMenu") &&
          (message?.insertMethod === DragDropInsertMethod.insertInto ||
            message?.insertMethod === DragDropInsertMethod.insertBefore ||
            message?.insertMethod === DragDropInsertMethod.insertAfter) &&
          sourceComponentDefinition?.name !== "MenuItem" &&
          sourceComponentDefinition?.name !== "SubMenu"))
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drop this item into Menu");
      return false;
    }

    if (
      (targetDI?.item.name === "Table" ||
        targetDI?.item.name === "ReportTable" ||
        targetDI?.item.name === "TableHeader" ||
        targetDI?.item.name === "ReportTableHeader" ||
        targetDI?.item.name === "TableRow" ||
        targetDI?.item.name === "ReportTableRow" ||
        targetDI?.item.name === "StaticTableRow") &&
      message?.insertMethod === DragDropInsertMethod.insertInto
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drop item table header or table row.");
      return false;
    }
    const elementsThatAreCouldBeDroppedToStacks = [
      "label",
      "button",
      "textinput",
      "image",
      "icon",
      "tooltip",
      "thumbnail",
      "barcoderenderer",
      "badge",
      "locationsearch",
      "numberinput",
      "email",
      "textarea",
      "currency",
      "password",
      "percent",
      "url",
      "autocomplete",
      "selectbox",
      "multiselect",
      "checkbox",
      "radiogroup",
      "switch",
      "contentmenu",
      "date",
      "daterange",
      "datetimerange",
      "datetime",
      "monthpicker",
      "yearpicker",
      "countup",
      "countdown",
      "rate",
      "language selector",
      "filedownload",
      "qrrenderer",
      "horizontalstack",
      "verticalstack",
      "dynamicInput",
      "colorpicker",
      "progressbar"
    ];

    let parentDI: IDisplayItem | undefined;

    if (targetDI) {
      parentDI = sdDict[this.getParentId(targetDI, sd?.displayItems) as number];
    }

    if (
      targetComponentDefinition &&
      targetComponentDefinition.name === "HorizontalStack" &&
      sourceComponentDefinition &&
      !elementsThatAreCouldBeDroppedToStacks.includes(sourceComponentDefinition.name?.toLowerCase()) &&
      message?.insertMethod === DragDropInsertMethod.insertInto
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drop this item into the stack.");
      return false;
    }
    if (
      parentDI &&
      parentDI.item?.name === "HorizontalStack" &&
      sourceComponentDefinition &&
      !elementsThatAreCouldBeDroppedToStacks.includes(sourceComponentDefinition.name?.toLowerCase())
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drop this item on the stack.");
      return false;
    }
    if (
      targetComponentDefinition &&
      targetComponentDefinition.name === "RadioButton" &&
      sourceComponentDefinition?.name !== "RadioButton"
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drop item on Radio Group.");
      return false;
    }
    if (
      (targetDI?.item?.name === "TableColumn" ||
        targetDI?.item?.name === "ReportTableColumn" ||
        targetDI?.item?.name === "EditableTableColumn" ||
        targetDI?.item?.name === "TableTemplateColumn" ||
        targetDI?.item?.name === "TableDefaultColumn" ||
        targetDI?.item?.name === "StaticTableColumn") &&
      (sourceDI?.item?.name === "TableColumn" ||
        sourceDI?.item?.name === "ReportTableColumn" ||
        sourceDI?.item?.name === "EditableTableColumn" ||
        sourceDI?.item?.name === "TableTemplateColumn" ||
        sourceDI?.item?.name === "TableDefaultColumn" ||
        sourceDI?.item?.name === "StaticTableColumn") &&
      message?.insertMethod === DragDropInsertMethod.insertInto
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drop table column in table column.");
      return false;
    }
    if (
      targetDI &&
      targetDI.item &&
      (targetDI?.item?.name === "TableColumn" ||
        targetDI?.item?.name === "ReportTableColumn" ||
        targetDI.item.name === "TableRow" ||
        targetDI.item.name === "ReportTableRow" ||
        targetDI.item.name === "TableHeader" ||
        targetDI.item.name === "ReportTableHeader" ||
        targetDI.item.name === "Table" ||
        targetDI.item.name === "ReportTable" ||
        targetDI.item.name === "TableTemplateColumn" ||
        targetDI.item.name === "TableDefaultColumn" ||
        targetDI.item.name === "StaticTableColumn") &&
      (sourceDI?.item?.name === "Panel" || sourceComponentDefinition?.name === "Panel") &&
      sd?.isAbsoluteDesign === true &&
      message?.insertMethod === DragDropInsertMethod.insertInto
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drop panel in table.");
      return false;
    }
    if (
      (targetDI?.item?.name === "TableColumn" ||
        targetDI?.item?.name === "EditableTableColumn" ||
        targetDI?.item?.name === "TableTemplateColumn" ||
        targetDI?.item?.name === "TableDefaultColumn" ||
        targetDI?.item?.name === "StaticTableColumn") &&
      (sourceDI?.item?.name === "Menu" || sourceComponentDefinition?.name === "Menu")
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not this item in table.");
      return false;
    }
    if (sd?.isAbsoluteDesign === true) {
      const targetDI = simpleTargetKuikaId ? sdDict[parseInt(simpleTargetKuikaId, 10)] : undefined;
      const sourceDI = simpleSourceKuikaId ? sdDict[parseInt(simpleSourceKuikaId, 10)] : undefined;
      if (
        targetDI &&
        targetDI.item &&
        targetDI.item.name === "Panel" &&
        (sourceDI?.item?.name === "Panel" || sourceComponentDefinition?.name === "Panel") &&
        message?.insertMethod === DragDropInsertMethod.insertInto
      ) {
        if (doNotTriggerNotifications !== true)
          KMainFunctions.displayWarningNotification("You can not drop panel to panel.");
        return false;
      }
      if (
        targetComponentDefinition &&
        targetComponentDefinition.name === "Panel" &&
        sourceComponentDefinition?.name === "Panel" &&
        message?.insertMethod === DragDropInsertMethod.insertInto
      ) {
        if (doNotTriggerNotifications !== true)
          KMainFunctions.displayWarningNotification("You can not drop panel to panel.");
        return false;
      }
    }

    if (
      (targetComponentDefinition?.isDataRepeater ||
        targetComponentDefinition?.name === "TableColumn" ||
        targetComponentDefinition?.name === "ReportTableColumn" ||
        targetComponentDefinition?.name === "EditableTableColumn" ||
        targetComponentDefinition?.name === "TableTemplateColumn" ||
        targetComponentDefinition?.name === "TableDefaultColumn" ||
        parentComponent?.name === "TableColumn" ||
        parentComponent?.name === "ReportTableColumn" ||
        parentComponent?.name === "EditableTableColumn" ||
        parentComponent?.name === "TableTemplateColumn" ||
        parentComponent?.name === "TableDefaultColumn" ||
        parentComponent?.name === "StaticTableColumn") &&
      (sourceComponentDefinition?.isDataRepeater ||
        (sourceComponentDefinition?.componentCategoryName === "Chart" &&
          ((sourceComponentDefinition?.name !== "ProgressBar" &&
            sourceComponentDefinition?.name !== "ProgressCircle") ||
            this.screenContext.projectProvider.getProject().projectType === EProjectType.Mobile)) ||
        sourceComponentDefinition?.componentCategoryName === "Special") &&
      targetComponentDefinition?.name !== "Table" &&
      sourceComponentDefinition.name !== "Table" &&
      sourceComponentDefinition.name !== "ContentMenu" &&
      sourceComponentDefinition?.componentCategoryName !== "Data" &&
      !(
        (targetComponentDefinition?.name === "TableColumn" ||
          targetComponentDefinition?.name === "ReportTableColumn" ||
          targetComponentDefinition?.name === "EditableTableColumn" ||
          targetComponentDefinition?.name === "TableTemplateColumn" ||
          targetComponentDefinition?.name === "TableDefaultColumn" ||
          targetComponentDefinition?.name === "StaticTableColumn") &&
        (sourceComponentDefinition?.name === "TableColumn" ||
          sourceComponentDefinition?.name === "ReportTableColumn" ||
          sourceComponentDefinition?.name === "EditableTableColumn" ||
          sourceComponentDefinition?.name === "TableTemplateColumn" ||
          sourceComponentDefinition?.name === "TableDefaultColumn" ||
          sourceComponentDefinition?.name === "StaticTableColumn")
      )
    ) {
      if (doNotTriggerNotifications !== true) {
        KMainFunctions.displayWarningNotification("You can not drop data repeater in data repeater.");
      }
      return false;
    }

    if (
      targetDI &&
      targetComponentDefinition?.isDataRepeater &&
      sourceComponentDefinition?.isDataRepeater &&
      this.getIsNestedDataRepeater(targetDI) &&
      (targetComponentDefinition.name === "TableColumn" ||
        targetComponentDefinition.name === "ReportTableColumn" ||
        targetComponentDefinition.name === "EditableTableColumn" ||
        targetComponentDefinition.name === "TableTemplateColumn" ||
        targetComponentDefinition.name === "TableDefaultColumn" ||
        targetComponentDefinition.name === "StaticTableColumn") &&
      sourceComponentDefinition.name !== "TableColumn" &&
      sourceComponentDefinition.name !== "ReportTableColumn" &&
      sourceComponentDefinition.name !== "EditableTableColumn" &&
      sourceComponentDefinition.name !== "TableTemplateColumn" &&
      sourceComponentDefinition.name !== "TableDefaultColumn" &&
      sourceComponentDefinition.name !== "StaticTableColumn"
    ) {
      if (doNotTriggerNotifications !== true) {
        KMainFunctions.displayWarningNotification("Nesting data repeaters more than one level is not allowed.");
      }

      return false;
    }

    if (
      targetComponentDefinition &&
      targetComponentDefinition.isDataRepeater &&
      sourceComponentDefinition &&
      sourceComponentDefinition.name === "Signature" &&
      (message?.insertMethod === DragDropInsertMethod.insertInto ||
        targetComponentDefinition.name === "TableColumn" ||
        targetComponentDefinition.name === "TableTemplateColumn" ||
        targetComponentDefinition.name === "TableDefaultColumn" ||
        targetComponentDefinition.name === "StaticTableColumn")
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drop signature on data repeater.");
      return false;
    }
    if (sourceDI && sourceDI.item && sourceDI.item.name === "TabItem" && targetDI?.item?.name !== "TabItem") {
      if (doNotTriggerNotifications !== true) KMainFunctions.displayWarningNotification("You can not drag tab item.");
      return false;
    }
    if (
      (sourceComponentDefinition?.componentCategoryName === "Text Input" ||
        sourceComponentDefinition?.componentCategoryName === "Numeric Input" ||
        sourceComponentDefinition?.componentCategoryName === "Date and Time Input" ||
        sourceComponentDefinition?.componentCategoryName === "Select Input") &&
      sourceComponentDefinition?.name !== "Switch" &&
      sourceComponentDefinition?.name !== "Checkbox" &&
      sourceComponentDefinition?.name !== "Rate" &&
      (targetComponentDefinition?.componentCategoryName === "Data" ||
        targetDI?.item?.name === "TableColumn" ||
        targetDI?.item?.name === "TableTemplateColumn" ||
        targetDI?.item?.name === "TableDefaultColumn" ||
        targetDI?.item?.name === "StaticTableColumn") &&
      this.screenContext.projectProvider.getProject().projectType === EProjectType.Mobile &&
      (message?.insertMethod === DragDropInsertMethod.insertInto ||
        message?.insertMethod === DragDropInsertMethod.insertAfter)
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag input to table column.");
      return false;
    }
    if (
      sourceDI &&
      sourceDI.item &&
      (sourceDI.item.name === "TableHeader" ||
        sourceDI.item.name === "ReportTableHeader" ||
        sourceDI.item.name === "StaticTableHeader")
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag table header.");
      return false;
    }
    if (
      sourceDI?.item?.name === "TableRow" ||
      sourceDI?.item?.name === "ReportTableRow" ||
      sourceDI?.item?.name === "StaticTableRow"
    ) {
      // Allow reordering StaticTableRow within the same StaticTable
      const isStaticTableRowReorder =
        sourceDI?.item?.name === "StaticTableRow" &&
        targetDI?.item?.name === "StaticTableRow" &&
        (message?.insertMethod === DragDropInsertMethod.insertBefore ||
          message?.insertMethod === DragDropInsertMethod.insertAfter);

      if (!isStaticTableRowReorder) {
        if (doNotTriggerNotifications !== true)
          KMainFunctions.displayWarningNotification("You can not drag table row.");
        return false;
      }
    }

    if (
      sourceDI &&
      sourceDI.item &&
      sourceDI.item.name === "CollapsePanel" &&
      targetDI?.item?.name !== "CollapsePanel"
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag Collapse Panel.");
      return false;
    }

    // KOA-13797: a SplitterPanel is a fixed slot of its Splitter, like a CollapsePanel.
    if (
      sourceDI &&
      sourceDI.item &&
      sourceDI.item.name === "SplitterPanel" &&
      targetDI?.item?.name !== "SplitterPanel"
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag Splitter Panel.");
      return false;
    }

    if (
      targetDI &&
      targetDI.item &&
      targetDI.item.name === "Row" &&
      sourceDI &&
      sourceDI.item &&
      sourceDI.item.name !== "Row" &&
      message?.insertMethod === DragDropInsertMethod.insertInto &&
      sourceDI?.item?.name !== "Col"
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag item on Row.");
      return false;
    }

    if (
      targetDI &&
      targetDI.item &&
      targetDI.item.name === "Panel" &&
      (sourceDI?.item?.name === "Table" ||
        sourceComponentDefinition?.name === "Table" ||
        sourceDI?.item?.name === "ReportTable" ||
        sourceComponentDefinition?.name === "ReportTable") &&
      sd?.isAbsoluteDesign === true &&
      message?.insertMethod === DragDropInsertMethod.insertInto
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag table on panel.");
      return false;
    }

    if (
      targetDI?.item?.name !== undefined &&
      (sourceDI?.item?.name === "Page Break" ||
        sourceComponentDefinition?.name === "Page Break" ||
        sourceDI?.item?.name === "Divider" ||
        sourceComponentDefinition?.name === "Divider") &&
      sd?.isAbsoluteDesign === true &&
      message?.insertMethod === DragDropInsertMethod.insertInto
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag divider here.");
      return false;
    }

    if (
      (sourceDI?.item?.name === "Page Break" ||
        sourceComponentDefinition?.name === "Page Break" ||
        sourceDI?.item?.name === "Divider" ||
        sourceComponentDefinition?.name === "Divider") &&
      targetDI?.item?.name !== "Panel" &&
      targetDI?.item?.name !== "ReportTable" &&
      targetDI?.item?.name !== "ReportHeader" &&
      targetDI?.item?.name !== "StaticTable" &&
      targetDI?.item?.name !== undefined &&
      sd?.isAbsoluteDesign === true &&
      (message?.insertMethod === DragDropInsertMethod.insertBefore ||
        message?.insertMethod === DragDropInsertMethod.insertAfter)
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag divider here.");
      return false;
    }

    if (
      targetDI &&
      targetDI.item &&
      (((targetDI.item.name === "Table" || targetDI.item.name === "ReportTable") && sourceDI?.item?.name === "Panel") ||
        (targetDI?.item?.name === "Panel" &&
          (sourceDI?.item?.name === "Table" || sourceDI?.item?.name === "ReportTable")) ||
        (targetDI?.item?.name === "Panel" && sourceDI?.item?.name === "Panel") ||
        ((targetDI.item.name === "Table" || targetDI.item.name === "ReportTable") &&
          (sourceDI?.item?.name === "Table" || sourceDI?.item?.name === "ReportTable")) ||
        ((targetDI.item.name === "Table" || targetDI.item.name === "ReportTable") &&
          sourceComponentDefinition?.name === "Panel") ||
        (targetDI?.item?.name === "Panel" &&
          (sourceComponentDefinition?.name === "Table" || sourceComponentDefinition?.name === "ReportTable"))) &&
      message?.insertMethod === DragDropInsertMethod.insertInto &&
      sd?.isAbsoluteDesign === true
    ) {
      if (doNotTriggerNotifications !== true) KMainFunctions.displayWarningNotification("You can not drag item here.");
      return false;
    }

    if (sourceComponentDefinition?.isDataRepeater && targetDI?.item.name === "ContentMenuPanel") {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag data repeater on content menu.");
      return false;
    }

    if (
      targetDI &&
      targetDI.item &&
      (targetDI.item.name === "Header" ||
        targetDI.item.name === "Footer" ||
        targetDI.item.name === "Page Header" ||
        targetDI.item.name === "Page Footer") &&
      (sourceDI?.item?.name === "Table" ||
        sourceComponentDefinition?.name === "Table" ||
        sourceDI?.item?.name === "ReportTable" ||
        sourceComponentDefinition?.name === "ReportTable" ||
        sourceDI?.item?.name === "QRRenderer" ||
        sourceComponentDefinition?.name === "QRRenderer" ||
        sourceDI?.item?.name === "BarcodeRenderer" ||
        sourceComponentDefinition?.name === "BarcodeRenderer" ||
        sourceComponentDefinition?.name === "Page Break" ||
        sourceComponentDefinition?.name === "Divider" ||
        sourceDI?.item?.name === "Page Break" ||
        sourceDI?.item?.name === "Divider" ||
        sourceComponentDefinition?.name === "Vertical Panel" ||
        sourceDI?.item?.name === "Vertical Panel" ||
        sourceComponentDefinition?.name === "Panel" ||
        sourceDI?.item?.name === "Panel" ||
        sourceComponentDefinition?.name === "Box" ||
        sourceDI?.item?.name === "Box") &&
      sd?.isAbsoluteDesign === true
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag table on header or footer.");
      return false;
    }

    if (
      targetDI &&
      targetDI.item &&
      (targetDI.item.name === "Header" ||
        targetDI.item.name === "Footer" ||
        targetDI.item.name === "Box" ||
        targetDI.item.name === "Page Header" ||
        targetDI.item.name === "Page Footer") &&
      (sourceDI?.item?.name === "Panel" || sourceComponentDefinition?.name === "Panel") &&
      sd?.isAbsoluteDesign === true
    ) {
      if (doNotTriggerNotifications !== true) KMainFunctions.displayWarningNotification("You can not drag panel here.");
      return false;
    }

    if (
      (sourceDI?.item.name === "ReportTableHeader" ||
        sourceDI?.item.name === "ReportTableRow" ||
        sourceDI?.item.name === "ReportTableColumn" ||
        sourceComponentDefinition?.name === "ReportTableHeader" ||
        sourceComponentDefinition?.name === "ReportTableRow" ||
        sourceComponentDefinition?.name === "ReportTableColumn" ||
        sourceDI?.item.name === "StaticTableHeader" ||
        sourceDI?.item.name === "StaticTableRow" ||
        sourceDI?.item.name === "StaticTableColumn" ||
        sourceComponentDefinition?.name === "StaticTableHeader" ||
        sourceComponentDefinition?.name === "StaticTableRow" ||
        sourceComponentDefinition?.name === "StaticTableColumn") &&
      targetDI?.item?.name === "Box" &&
      sd?.isAbsoluteDesign === true
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag this item into box.");
      return false;
    }

    if (
      (sourceDI?.item.name === "ReportTable" ||
        sourceDI?.item.name === "ReportTableHeader" ||
        sourceDI?.item.name === "ReportTableRow" ||
        sourceDI?.item.name === "ReportTableColumn" ||
        sourceComponentDefinition?.name === "ReportTable" ||
        sourceComponentDefinition?.name === "ReportTableHeader" ||
        sourceComponentDefinition?.name === "ReportTableRow" ||
        sourceComponentDefinition?.name === "ReportTableColumn" ||
        sourceDI?.item.name === "StaticTable" ||
        sourceDI?.item.name === "StaticTableHeader" ||
        sourceDI?.item.name === "StaticTableRow" ||
        sourceDI?.item.name === "StaticTableColumn" ||
        sourceComponentDefinition?.name === "StaticTable" ||
        sourceComponentDefinition?.name === "StaticTableHeader" ||
        sourceComponentDefinition?.name === "StaticTableRow" ||
        sourceComponentDefinition?.name === "StaticTableColumn") &&
      targetDI?.item?.name === "Vertical Panel"
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag this item into vertical panel.");
      return false;
    }

    if (
      targetDI &&
      targetDI.item &&
      (targetDI.item.name === "EditableTableColumn" || targetDI.item.name === "TableDefaultColumn") &&
      sourceDI?.item?.name !== "TableColumn" &&
      sourceDI?.item.name !== "EditableTableColumn" &&
      sourceDI?.item.name !== "TableTemplateColumn" &&
      sourceDI?.item.name !== "TableDefaultColumn"
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag element into Table Default Column.");
      return false;
    }

    if (
      parentDI &&
      parentDI.item &&
      (parentDI.item.name === "EditableTableColumn" ||
        parentDI.item.name === "TableDefaultColumn" ||
        parentDI.item.name === "StaticTableColumn") &&
      (message?.insertMethod === DragDropInsertMethod.insertBefore ||
        message?.insertMethod === DragDropInsertMethod.insertAfter)
    ) {
      if (doNotTriggerNotifications !== true)
        KMainFunctions.displayWarningNotification("You can not drag element into Table Default Column.");
      return false;
    }
    if (
      targetComponent &&
      targetComponentDefinition &&
      targetComponentDefinition.componentType === ComponentType.responsiveColumn &&
      sourceComponentDefinition &&
      sourceComponentDefinition.componentType !== ComponentType.responsiveColumn &&
      (message?.insertMethod === DragDropInsertMethod.insertAfter ||
        message?.insertMethod === DragDropInsertMethod.insertBefore)
    ) {
      // row içerisindeki columnların aralarına eklenmesini engeller.
      return false;
    }
    return true;
  };

  placeToRootLastItem = (sd: IScreenDesign, sourceDI: IDisplayItem, nextSelectedKuikaId: string) => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    if (sd) {
      const rootDI = dict[SpecialDIIds.rootDI];
      if (rootDI === undefined || KMainFunctions.isObjectEmpty(rootDI)) {
        sd = ScreenDesignHelper.createRootDI(sd, this.screenContext.screenProvider.getScreenDesignDictionary());
      }
      if (sourceDI.parentId === undefined) {
        const rootDIItem = dict[SpecialDIIds.rootDI];
        sourceDI.parentId = rootDIItem.id;
      }

      const rootDiIdx = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);
      if (rootDiIdx >= 0) sd.displayItems[rootDiIdx].children.push(sourceDI);

      this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropDi, nextSelectedKuikaId);
    }
  };

  placeToRootFirstItem = (sd: IScreenDesign, sourceDI: IDisplayItem, nextSelectedKuikaId: string) => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    if (sd) {
      const rootDI = dict[SpecialDIIds.rootDI];
      if (rootDI === undefined || KMainFunctions.isObjectEmpty(rootDI)) {
        sd = ScreenDesignHelper.createRootDI(sd, this.screenContext.screenProvider.getScreenDesignDictionary());
      }
      if (sourceDI.parentId === undefined) {
        const rootDIItem = dict[SpecialDIIds.rootDI];
        sourceDI.parentId = rootDIItem.id;
      }

      const rootDiIdx = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);
      if (rootDiIdx >= 0) sd.displayItems[rootDiIdx].children.splice(0, 0, sourceDI);

      this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropDi, nextSelectedKuikaId);
    }
  };

  placeToBeforeItem = (
    sd: IScreenDesign,
    targetDI: IDisplayItem,
    sourceDI: IDisplayItem,
    nextSelectedKuikaId: string,
    sourceDIParentId?: number,
    isElementDraggable?: boolean
  ) => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    if (sd && targetDI) {
      const parentOfSourceDI = sourceDIParentId ? dict[sourceDIParentId] : undefined;
      if (targetDI.parentId) {
        const parentId = isElementDraggable
          ? this.getParentId(sourceDI, sd.displayItems)
          : this.getParentId(targetDI, sd.displayItems);
        const parent = parentId ? dict[parentId] : undefined;
        if (parent) {
          const idx = parent.children.indexOf(targetDI);
          parent.children.splice(idx, 0, sourceDI);
        }
      } else {
        const rootDiIdx = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);
        if (rootDiIdx >= 0) {
          const idx = sd.displayItems[rootDiIdx].children.indexOf(targetDI);
          sd.displayItems[rootDiIdx].children.splice(idx, 0, sourceDI);
        }
      }
      if (
        targetDI &&
        targetDI.item &&
        targetDI.item.name === "Col" &&
        sourceDI &&
        sourceDI.item &&
        sourceDI.item.name === "Col"
      ) {
        if (
          parentOfSourceDI &&
          parentOfSourceDI.item &&
          parentOfSourceDI.item.name === "Row" &&
          (!parentOfSourceDI.children || parentOfSourceDI.children.length === 0)
        ) {
          this.deleteItemByKuikaId(parentOfSourceDI.id.toString());
        }
      }
      if (sourceDI.parentId === undefined) {
        const rootDIItem = dict[SpecialDIIds.rootDI];
        sourceDI.parentId = rootDIItem.id;
      }
      this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropDi, nextSelectedKuikaId);
    }
  };

  placeAfterSpecificItem = (
    sd: IScreenDesign,
    targetDI: IDisplayItem,
    sourceDI: IDisplayItem,
    nextSelectedKuikaId,
    sourceDIParentId?: number,
    isElementDraggable?: boolean
  ) => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    if (sd && targetDI) {
      // const parentOfSourceDI = ScreenDesignHelper.findDisplayItemBykuikaid(
      //   sourceDIParentId?.toString(),
      //   sd.displayItems
      // );
      const parentOfSourceDI = sourceDIParentId ? dict[sourceDIParentId] : undefined;
      if (targetDI.parentId) {
        const parentId = isElementDraggable
          ? this.getParentId(sourceDI, sd.displayItems)
          : this.getParentId(targetDI, sd.displayItems);
        const parent = parentId ? dict[parentId] : undefined;
        if (parent) {
          let idx = parent.children.indexOf(targetDI);
          idx += 1;
          parent.children.splice(idx, 0, sourceDI);
        }
      } else {
        const rootDiIdx = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);
        if (rootDiIdx >= 0) {
          let idx = sd.displayItems[rootDiIdx].children.indexOf(targetDI);
          idx += 1;
          sd.displayItems[rootDiIdx].children.splice(idx, 0, sourceDI);
        }
      }
      if (
        targetDI &&
        targetDI.item &&
        targetDI.item.name === "Col" &&
        sourceDI &&
        sourceDI.item &&
        sourceDI.item.name === "Col"
      ) {
        if (
          parentOfSourceDI &&
          parentOfSourceDI.item &&
          parentOfSourceDI.item.name === "Row" &&
          (!parentOfSourceDI.children || parentOfSourceDI.children.length === 0)
        ) {
          this.deleteItemByKuikaId(parentOfSourceDI.id.toString());
        }
      }
      if (sourceDI.parentId === undefined) {
        const rootDIItem = dict[SpecialDIIds.rootDI];
        sourceDI.parentId = rootDIItem.id;
      }
      this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropDi, nextSelectedKuikaId);
    }
  };

  /**
   * Adds a `componentName` child to `kuikaId` and selects it — or, like `deleteItemByKuikaId`, selects
   * `kuikaIdToFocus` instead. A caller that adds a component on the user's behalf while they are
   * editing something else (the table-column edit-mode swap) keeps them where they are with it.
   */
  addChildItemByComponentName = (
    kuikaId: string,
    componentName: string,
    placeToType?: PlaceToType,
    kuikaIdToFocus?: string
  ): void => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    const dashboardState = this.screenContext.designSituationProvider?.state?.dashboardState;
    const isReportDesignComponentAllowed = dashboardState === DashboardState.reportDesigner;
    const component: IDisplayItemComponent | undefined = ScreenDesignHelper.createNewComponentByName(
      componentName,
      this.screenContext.state.project,
      isReportDesignComponentAllowed
    );
    if (!component) {
      return;
    }
    const newComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(component);
    if (!newComponentDefinition) {
      return;
    }
    const sd = this.screenContext.screenProvider.getScreenDesign();
    const targetDI = dict[parseInt(kuikaId, 10)];
    if (!targetDI) {
      return;
    }
    let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
      sd,
      this.screenContext.screenProvider.getScreenDesignDictionary(),
      targetDI.id,
      component,
      this.screenContext.state.project.componentSetObj.components
    );
    newDI = ScreenDesignHelper.addDefaultComponentStylings(
      newDI,
      newComponentDefinition,
      this.screenContext.defaultStyleProvider
    );

    ScreenDesignHelper.addChildForItemContainer(
      component,
      newDI,
      sd,
      this.screenContext.screenProvider.getScreenDesignDictionary(),
      this.screenContext.state.project.componentSetObj.components,
      this.screenContext.projectProvider.getProject().projectType,
      this.screenContext.defaultStyleProvider
    );

    // Shared with the child-items editor's screen-agnostic add path, so a column created from the
    // right panel and one created by a drag arrive the same size. See `applyChildItemSizeDefaults`.
    applyChildItemSizeDefaults(newDI, newComponentDefinition?.name);
    if (placeToType && placeToType === PlaceToType.PlaceToFirstChildItem) {
      this.placeToFirstChildItem(sd, targetDI, newDI, kuikaIdToFocus);
    } else {
      this.placeToLastChildItem(sd, targetDI, newDI, kuikaIdToFocus);
    }
  };

  deleteItemByKuikaId = (kuikaId: string, kuikaIdToFocus?: string) => {
    this.removeItem(kuikaId, kuikaIdToFocus);
  };

  reOrderItems = (sourceKuikaId: string, targetKuikaId: string, nextSelectedDiId: string) => {
    this.dragDropByMoveItemMessage({
      sourceKuikaId,
      targetKuikaId,
      insertMethod: DragDropInsertMethod.insertBefore,
      nextSelectedDiId
    } as IDragDropByMoveItemMessage);
  };

  placeToLastChildItem = (
    sd: IScreenDesign,
    targetDI: IDisplayItem,
    sourceDI: IDisplayItem,
    kuikaIdToFocus?: string
  ): void => {
    if (sd && targetDI && targetDI.children) {
      targetDI.children.push(sourceDI);
      const rootDiIdx = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);
      if (rootDiIdx >= 0) {
        const idx = sd.displayItems[rootDiIdx].children.indexOf(targetDI);
        if (idx !== -1) {
          sd.displayItems[rootDiIdx].children[idx] = targetDI;
        }
      }
      this.screenContext.screenProvider.setScreenDesign(
        sd,
        ESdChangeReason.DragDropDi,
        kuikaIdToFocus || sourceDI?.id.toString()
      );
    }
  };

  placeToFirstChildItem = (
    sd: IScreenDesign,
    targetDI: IDisplayItem,
    sourceDI: IDisplayItem,
    kuikaIdToFocus?: string
  ): void => {
    if (sd && targetDI && targetDI.children) {
      targetDI.children.unshift(sourceDI);
      const rootDiIdx = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);
      if (rootDiIdx >= 0) {
        const idx = sd.displayItems[rootDiIdx].children.indexOf(targetDI);
        if (idx !== -1) {
          sd.displayItems[rootDiIdx].children[idx] = targetDI;
        }
      }
      this.screenContext.screenProvider.setScreenDesign(
        sd,
        ESdChangeReason.DragDropDi,
        kuikaIdToFocus || sourceDI?.id.toString()
      );
    }
  };

  removePanelDiIfEmpty = (sd: IScreenDesign, sourceDI: IDisplayItem, targetDI?: IDisplayItem) => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    if (!sourceDI || !sourceDI?.parentId) return;
    const parentId = this.getParentId(sourceDI, sd.displayItems);
    const parentDI = parentId ? dict[parentId] : undefined;
    if (
      sd.isAbsoluteDesign &&
      parentDI &&
      parentDI.item?.name === "Panel" &&
      (!parentDI.children || parentDI.children.length === 0)
    ) {
      const rootDiIdx = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);
      let idx = sd.displayItems[rootDiIdx].children.indexOf(parentDI);
      if (targetDI && targetDI.id === parentDI.id) {
        return;
      }
      if (idx === sd.displayItems[rootDiIdx].children.length - 1 && targetDI?.id === SpecialDIIds.rootDI) {
        return;
      }
      if (idx === sd.displayItems[rootDiIdx].children.length - 1) {
        this.removeItem(parentDI.id.toString());
      }
    }
  };

  findNavDiIdsRecursive = (di: IDisplayItem, navDiIds: number[]) => {
    di.item?.events?.forEach((e) => {
      e.actions.forEach((a) => {
        if (a.label === "NAVIGATE" || a.label === "NavigateToTask") {
          const navKuikaIdParam = a.parameters.find(
            (x) => x.name === "navigationKuikaId" && x.symbol.fixedValue && x.symbol.fixedValue !== ""
          );
          if (navKuikaIdParam) {
            navDiIds.push(navKuikaIdParam.symbol.fixedValue);
          }
        }
      });
    });

    di.children?.forEach((c) => {
      this.findNavDiIdsRecursive(c, navDiIds);
    });
  };

  getParentId = (di: IDisplayItem, allDisplayItems: IDisplayItem[]): number | undefined => {
    if (
      di.id === SpecialDIIds.headerDI ||
      di.id === SpecialDIIds.footerDI ||
      di.id === SpecialDIIds.pageHeaderDI ||
      di.id === SpecialDIIds.pageFooterDI
    ) {
      return undefined;
    }

    let parentId: number | undefined = localStorage.getItem("simulateCorruptedParentId") === "true" ? -1 : di.parentId;

    if (
      !di.parentId ||
      (localStorage.getItem("disableParentIdMitigation") !== "true" && parentId === SpecialDIIds.rootDI)
    ) {
      const parent: IDisplayItem | undefined = ScreenDesignHelper.findDisplayItemParentByKuikaId(
        di.id.toString(),
        allDisplayItems
      );

      if (parent) {
        parentId = parent.id;
      }
    }

    return parentId;
  };

  removeItem = (kuikaid: string, kuikaIdToFocus?: string) => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();

    if (
      kuikaid === SpecialDIIds.rootDI.toString() ||
      kuikaid === SpecialDIIds.layoutHeaderDI.toString() ||
      kuikaid === SpecialDIIds.layoutFooterDI.toString() ||
      kuikaid === SpecialDIIds.layoutLeftDI.toString() ||
      kuikaid === SpecialDIIds.layoutRightDI.toString()
    ) {
      return;
    }

    const sd: IScreenDesign = this.screenContext.screenProvider.getScreenDesign();

    if (!sd) {
      return;
    }

    // const di: IDisplayItem | undefined = ScreenDesignHelper.findDisplayItemBykuikaid(kuikaid, sd.displayItems);
    const di = dict[parseInt(kuikaid, 10)];

    if (!di) {
      return;
    }

    if (ScreenDesignHelper.getAnyChildsInteractionDisabled(parseInt(kuikaid, 10), sd.displayItems)) {
      KMainFunctions.displayWarningNotification(
        `Display item has a child with disabled interaction. You can not delete it.`
      );
      return;
    }

    const navDiIdsToRemove: number[] = [];
    this.findNavDiIdsRecursive(di, navDiIdsToRemove);

    let indexToBeDeleted: number | null = null;

    let parentId: number | undefined = this.getParentId(di, sd.displayItems);

    if (parentId) {
      const parent = dict[parentId];

      if (parent) {
        const parentComponent: IDisplayItemComponent | undefined = parent?.item;

        if (parentComponent) {
          const parentComponentDefinition = this.getComponentDefinition(parentComponent);

          if (parentComponentDefinition?.children && parentComponentDefinition?.children.length > 0) {
            if (parent.children?.length === 1) {
              if (parent?.id) {
                this.removeItem(parent.id.toString());
              }
              return;
            }

            const fixedChild = parentComponentDefinition.childrenInfo?.find(
              (p) => p.name === di?.item?.name && p.fixed === true
            );

            if (fixedChild) {
              this.removeItem(parent.id.toString());
            }
          }
        }

        indexToBeDeleted = parent.children.indexOf(di);

        if (indexToBeDeleted !== null && indexToBeDeleted !== -1) {
          parent.children.splice(indexToBeDeleted, 1);
        }
      }
    } else if (
      kuikaid === SpecialDIIds.headerDI.toString() ||
      kuikaid === SpecialDIIds.footerDI.toString() ||
      kuikaid === SpecialDIIds.pageHeaderDI.toString() ||
      kuikaid === SpecialDIIds.pageFooterDI.toString()
    ) {
      indexToBeDeleted = sd.displayItems.indexOf(di);
      sd.displayItems.splice(indexToBeDeleted, 1);
    } else {
      const rootDiIdx: number = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);

      if (rootDiIdx >= 0) {
        indexToBeDeleted = sd.displayItems[rootDiIdx].children.indexOf(di);

        if (indexToBeDeleted !== null && indexToBeDeleted !== -1) {
          sd.displayItems[rootDiIdx].children.splice(indexToBeDeleted, 1);
        }
      }
    }

    const rootDiIdx = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);

    if (rootDiIdx !== -1) {
      sd.displayItems[rootDiIdx].children = sd.displayItems[rootDiIdx].children.filter(
        (x) => navDiIdsToRemove.indexOf(x.id) === -1
      );
    }

    if (parentId === undefined) {
      parentId = SpecialDIIds.rootDI;
    }

    this.removePanelDiIfEmpty(sd, di);

    this.screenContext.startProcessTriggerSync.pruneRemovedDisplayItemSubtree(di, sd?.id);

    this.screenContext.screenProvider.setScreenDesign(
      sd,
      ESdChangeReason.DragDropDi,
      kuikaIdToFocus || parentId.toString()
    );
  };

  cloneDI = (kuikaid: string) => {
    if (kuikaid === SpecialDIIds.rootDI.toString()) return;
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    const sd: IScreenDesign = this.screenContext.screenProvider.getScreenDesign();
    const di = dict[parseInt(kuikaid, 10)];
    const isEditableTableColumnChild = CommonHelperFunctions.getIsEditableTableColumnChild(kuikaid, dict);
    if (isEditableTableColumnChild) {
      return;
    }
    if (di && this.isFixed(di) == true) {
      return;
    }
    if (di && KMainFunctions.isObjectEmpty(di) === false) {
      KMainFunctions.cloneDisplayItemWithChildren(sd, dict, di, this.getReportPageWidth(sd));
      this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropDi, kuikaid);
    }
  };

  /** The report page's width (paper, not the zoomed frame), or null for a design without a page. */
  private getReportPageWidth = (sd: IScreenDesign): number | null => {
    const entry: IScreenCanvasSize | undefined = this.screenContext.projectProvider
      .getProject()
      ?.screenCanvasSizes?.screens?.find((screen: IScreenCanvasSize): boolean => screen.id === sd?.id);
    return hasExplicitReportPage(entry) ? getReportCanvasSize(entry).width : null;
  };

  isFixed = (di: IDisplayItem) => {
    const parentId = this.getParentId(di, this.screenContext.screenProvider.getScreenDesign().displayItems);

    if (parentId) {
      const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
      const parent = dict[parentId];
      if (parent) {
        const parentCompDef = this.screenContext.displayItemProvider.getComponentDefinition(parent.item);
        if (parentCompDef) {
          const childInfo = parentCompDef.childrenInfo?.find((p) => p.name == di.item.name);
          if (childInfo?.fixed === true) {
            return true;
          }
        }
      }
    }
    return false;
  };

  getComponentDefinition = (
    displayItemComponent: IDisplayItemComponent | undefined
  ): IComponentDefinition | undefined => {
    let component: IComponentDefinition | undefined;
    if (this.screenContext && this.screenContext.displayItemProvider.getComponentDefinition) {
      component = this.screenContext.displayItemProvider.getComponentDefinition(displayItemComponent);
    }
    return component;
  };

  getPropertyDefinition = (
    displayItemComponent: IDisplayItemComponent,
    diProperty: IDisplayItemProperty
  ): IPropertyDefinition | undefined => {
    let property: IPropertyDefinition | undefined;
    if (this.screenContext && this.screenContext.propertyProvider.getPropertyDefinition) {
      property = this.screenContext.propertyProvider.getPropertyDefinition(displayItemComponent, diProperty);
    }
    return property;
  };

  setColumnsNumber = (
    columnsNumber: number[],
    kuikaid: string,
    deviceType: DeviceType,
    isTabletDisable: boolean,
    isPhoneDisable: boolean
  ) => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    const sd: IScreenDesign = this.screenContext.screenProvider.getScreenDesign();
    const di = dict[parseInt(kuikaid, 10)];
    const colsNumber = columnsNumber.length;
    if (di && sd && colsNumber > 0) {
      if (di.children && di.children.length < colsNumber) {
        const numberOfMissingColumns = colsNumber - di.children.length;
        const displayItemComponent = di.children[0].item;

        const component: IDisplayItemComponent | undefined = displayItemComponent;
        if (component) {
          const newComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(component);
          for (var i = 0; i < numberOfMissingColumns; i++) {
            let columnDI = ScreenDesignHelper.createNewDisplayItemByComponent(
              sd,
              this.screenContext.screenProvider.getScreenDesignDictionary(),
              di.id,
              component,
              this.screenContext.state.project.componentSetObj.components
            );
            if (newComponentDefinition) {
              columnDI = ScreenDesignHelper.addDefaultComponentStylings(
                columnDI,
                newComponentDefinition,
                this.screenContext.defaultStyleProvider
              );
            }

            di.children.push(columnDI);
          }
        }
      } else if (di.children && di.children.length > colsNumber) {
        let children: IDisplayItem[];
        children = [];
        for (i = colsNumber; i < di.children.length; i++) {
          const currentCol = di.children[i];
          if (currentCol && currentCol.children.length > 0) {
            children.push(...currentCol.children);
          }
        }
        children.forEach((x) => {
          if (di) {
            x.parentId = di.children[colsNumber - 1].id;
          }
        });
        di.children[colsNumber - 1].children.push(...children);
        di.children.length = colsNumber;
      }
      let counter = 0;
      if (di && di.children) {
        di.children.forEach((ch) => {
          const item = ch?.item;
          const colNum = columnsNumber[counter];
          let colProperties = item.properties.filter((p) => p.selectedType === EPropertyType.column);
          // BEG - TO DO: this need to be removed in the future. this only to handle the old local col component.
          if (colProperties.length < 1) {
            colProperties = item.properties.filter((p) => this.isItColumnProperty(item, p) === true);
          }
          // END - TO DO:
          colProperties.forEach((p) => {
            const prop = this.getPropertyDefinition(item, p);
            if (prop) {
              const colTypeOptions = prop.typeOptions.find((x) => x.propertyType === EPropertyType.column.toString());
              if (
                (colTypeOptions && colTypeOptions.deviceTypeForColumnAttribute === deviceType) ||
                (isTabletDisable === true &&
                  colTypeOptions &&
                  colTypeOptions.deviceTypeForColumnAttribute === DeviceType.Tablet) ||
                (isPhoneDisable === true &&
                  colTypeOptions &&
                  colTypeOptions.deviceTypeForColumnAttribute === DeviceType.Phone)
              ) {
                const newVal = KMainFunctions.getResponsiveColumnStringValue(
                  p.symbol.fixedValue,
                  Number(Math.round(colNum * this.screenContext.state.project.componentSetObj.gridSystemCount))
                );
                p.symbol.fixedValue = newVal;
              }
            }
          });
          counter += 1;
        });
        let idx = -1;
        if (di.parentId) {
          const parentId = this.getParentId(di, sd.displayItems);
          const parent = parentId ? dict[parentId] : undefined;
          if (parent) {
            idx = parent.children.indexOf(di);
            parent.children[idx] = di;
          }
        } else {
          const rootDiIdx = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);
          if (rootDiIdx >= 0) {
            idx = sd.displayItems[rootDiIdx].children.indexOf(di);
            sd.displayItems[rootDiIdx].children[idx] = di;
          }
        }
      }
      this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.ChangeResponsiveRowColumnSettings, kuikaid);
    }
  };

  setColPropertiesForSmallSizes = (
    type: ResponsiveTypes,
    kuikaId: string,
    deviceType: DeviceType,
    isTabletDisable: boolean,
    isPhoneDisable: boolean
  ) => {
    const sd: IScreenDesign = this.screenContext.screenProvider.getScreenDesign();
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    // const di = ScreenDesignHelper.findDisplayItemBykuikaid(kuikaId, sd.displayItems);
    const di = dict[parseInt(kuikaId, 10)];
    if (di && di.children) {
      di.children.forEach((ch) => {
        const item = ch?.item;
        const colProperties = item.properties.filter((p) => p.selectedType === EPropertyType.column);
        colProperties.forEach((p, colIdx) => {
          const prop = this.getPropertyDefinition(item, p);
          if (prop) {
            const colTypeOptions = prop.typeOptions.find((x) => x.propertyType === EPropertyType.column.toString());
            if (
              colTypeOptions &&
              (colTypeOptions.deviceTypeForColumnAttribute === deviceType ||
                (deviceType === DeviceType.Desktop &&
                  isTabletDisable === true &&
                  colTypeOptions.deviceTypeForColumnAttribute === DeviceType.Tablet) ||
                (deviceType === DeviceType.Desktop &&
                  isPhoneDisable === true &&
                  colTypeOptions.deviceTypeForColumnAttribute === DeviceType.Phone))
            ) {
              const newVal = KMainFunctions.getResponsiveColumnStringValue(
                p.symbol.fixedValue,
                Number(
                  Math.round(
                    this.getResponsiveTypeSizeFromColIdx(type, colIdx) *
                      this.screenContext.state.project.componentSetObj.gridSystemCount
                  )
                )
              );
              p.symbol.fixedValue = newVal;
            }
          }
        });
      });
    }
    this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.ChangeResponsiveRowColumnSettings, kuikaId);
  };

  getResponsiveTypeSizeFromColIdx = (type: ResponsiveTypes, idx: number): number => {
    let retVal = 0;

    switch (type) {
      case ResponsiveTypes.type1:
        retVal = 1;
        break;
      case ResponsiveTypes.type2:
        retVal = 1 / 2;
        break;
      case ResponsiveTypes.type3:
        retVal = 1 / 3;
        break;
      case ResponsiveTypes.type4:
        retVal = 1 / 4;
        break;
      case ResponsiveTypes.type5:
        retVal = 1 / 5;
        break;
      case ResponsiveTypes.type6:
        if (idx === 0) retVal = 2 / 3;
        else if (idx === 0) retVal = 1 / 3;
        break;
      case ResponsiveTypes.type7:
        if (idx === 0 || idx == 2) retVal = 1 / 4;
        else if (idx === 1) retVal = 2 / 4;
        break;
      case ResponsiveTypes.type8:
        if (idx === 0 || idx == 3) retVal = 1 / 6;
        else if (idx === 1 || idx == 2) retVal = 2 / 6;
        break;
      case ResponsiveTypes.type9:
        if (idx === 0) retVal = 1 / 3;
        else if (idx === 0) retVal = 2 / 3;
        break;
      case ResponsiveTypes.type1:
        retVal = 1;
        break;
      case ResponsiveTypes.type2:
        retVal = 1 / 2;
        break;
      case ResponsiveTypes.type3:
        retVal = 1 / 3;
        break;
      case ResponsiveTypes.type4:
        retVal = 1 / 4;
        break;

      default:
        break;
    }

    return retVal;
  };

  resizeColumns = (
    rowKuikaId: string,
    columnIndex: number,
    size: number,
    rightSize: number,
    isTabletDisable: boolean,
    isPhoneDisable: boolean
  ) => {
    const sd: IScreenDesign = this.screenContext.screenProvider.getScreenDesign();
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    const rowDI = dict[parseInt(rowKuikaId, 10)];

    if (rowDI && rowDI.children) {
      this.resizeColumnValuesByIndex(rowDI, DeviceType.Desktop, columnIndex, size, isTabletDisable, isPhoneDisable);
      this.resizeColumnValuesByIndex(
        rowDI,
        DeviceType.Desktop,
        columnIndex + 1,
        rightSize,
        isTabletDisable,
        isPhoneDisable
      );
    }
    this.screenContext.screenProvider.setScreenDesign(
      sd,
      ESdChangeReason.ChangeResponsiveRowColumnSettings,
      rowKuikaId
    );
  };

  splitColumn = (
    rowKuikaId: string,
    columnIndex: number,
    size: number,
    rightSize: number,
    isTabletDisable: boolean,
    isPhoneDisable: boolean
  ) => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    const sd: IScreenDesign = this.screenContext.screenProvider.getScreenDesign();
    const newColIdx = columnIndex + 1;
    const rowDI = dict[parseInt(rowKuikaId, 10)];
    if (rowDI && rowDI.children) {
      this.resizeColumnValuesByIndex(rowDI, DeviceType.Desktop, columnIndex, size, isTabletDisable, isPhoneDisable);
      const newColComponent = rowDI.children[columnIndex].item;
      const component: IDisplayItemComponent | undefined = newColComponent;
      if (component) {
        const newComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(component);

        let newColumnDI = ScreenDesignHelper.createNewDisplayItemByComponent(
          sd,
          this.screenContext.screenProvider.getScreenDesignDictionary(),
          rowDI.id,
          component,
          this.screenContext.state.project.componentSetObj.components
        );
        if (newComponentDefinition) {
          newColumnDI = ScreenDesignHelper.addDefaultComponentStylings(
            newColumnDI,
            newComponentDefinition,
            this.screenContext.defaultStyleProvider
          );
        }
        rowDI.children.splice(newColIdx, 0, newColumnDI);
        this.resizeColumnValuesByIndex(
          rowDI,
          DeviceType.Desktop,
          newColIdx,
          rightSize,
          isTabletDisable,
          isPhoneDisable
        );
      }
    }
    this.screenContext.screenProvider.setScreenDesign(
      sd,
      ESdChangeReason.ChangeResponsiveRowColumnSettings,
      rowKuikaId
    );
    const parentDI = this.screenContext.displayItemProvider.setComponentChildrenSettingsMainDi(rowKuikaId);
    this.screenContext.setState({ componentChildrenSettingsMainDi: parentDI });
  };

  resizeColumnValuesByIndex = (
    rowDI: IDisplayItem,
    deviceType: DeviceType,
    index: number,
    value: number,
    isTabletDisable: boolean,
    isPhoneDisable: boolean
  ) => {
    const { item } = rowDI.children[index];
    const colProperties = item.properties.filter((p) => p.selectedType === EPropertyType.column);
    colProperties.forEach((p) => {
      const prop = this.getPropertyDefinition(item, p);
      if (prop) {
        const colTypeOptions = prop.typeOptions.find((x) => x.propertyType === EPropertyType.column.toString());
        if (
          (colTypeOptions && colTypeOptions.deviceTypeForColumnAttribute === deviceType) ||
          (isTabletDisable === true &&
            colTypeOptions &&
            colTypeOptions.deviceTypeForColumnAttribute === DeviceType.Tablet) ||
          (isPhoneDisable === true &&
            colTypeOptions &&
            colTypeOptions.deviceTypeForColumnAttribute === DeviceType.Phone)
        ) {
          const newVal = KMainFunctions.getResponsiveColumnStringValue(p.symbol.fixedValue, value);
          p.symbol.fixedValue = newVal;
        }
      }
    });
  };

  isItColumnProperty = (item: IDisplayItemComponent, p: IDisplayItemProperty) => {
    const prop = this.getPropertyDefinition(item, p);
    if (prop) {
      if (
        prop.name === "xs" ||
        prop.name === "sm" ||
        prop.name === "md" ||
        prop.name === "lg" ||
        prop.name === "xl" ||
        prop.name === "xl"
      ) {
        return true;
      }
    }
    return false;
  };

  isDIChildComponent = (DI: IDisplayItem | undefined): boolean => {
    if (DI === undefined) return false;
    const { item } = DI;
    const itemDefinition = this.getComponentDefinition(item);
    if (itemDefinition === undefined) return false;
    return itemDefinition.isItChildItem;
  };

  // The rule lives in `component-children-host`, shared with the workflow screen builder so the
  // right panel and the Forms tab classify the catalog the same way.
  isDIChildComponentForParent = (DI: IDisplayItem | undefined, parentDI: IDisplayItem | undefined): boolean =>
    isDeclaredChildItem(DI, parentDI, (item) => this.getComponentDefinition(item));

  addNavigationComponent = (componentId: string, selectedKuikaId: string): IDisplayItem | undefined => {
    const sd = this.screenContext.screenProvider.getScreenDesign();
    const newComponent: IDisplayItemComponent | undefined = ScreenDesignHelper.createNewComponentById(
      componentId,
      this.screenContext
    );
    if (!newComponent) {
      return;
    }
    const newComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(newComponent);
    if (!newComponentDefinition) {
      return;
    }
    if (newComponent) {
      let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
        sd,
        this.screenContext.screenProvider.getScreenDesignDictionary(),
        SpecialDIIds.rootDI,
        newComponent,
        this.screenContext.state.project.componentSetObj.components
      );
      newDI = ScreenDesignHelper.addDefaultComponentStylings(
        newDI,
        newComponentDefinition,
        this.screenContext.defaultStyleProvider
      );
      this.placeToRootLastItem(sd, newDI, selectedKuikaId);
      return newDI;
    }
  };

  createHeaderDI = (headerId: string) => {
    this.createFixedPanelDI(headerId, SpecialDIIds.headerDI, ComponentType.fixedHeader);
  };

  createFooterDI = (footerId: string) => {
    this.createFixedPanelDI(footerId, SpecialDIIds.footerDI, ComponentType.fixedFooter);
  };

  createPageHeaderDI = (pageHeaderId: string) => {
    this.createFixedPanelDI(pageHeaderId, SpecialDIIds.pageHeaderDI, ComponentType.pageHeader);
  };

  createPageFooterDI = (pageFooterId: string) => {
    this.createFixedPanelDI(pageFooterId, SpecialDIIds.pageFooterDI, ComponentType.pageFooter);
  };

  createFixedPanelDI = (id: string, specialDIId: SpecialDIIds, componentType: ComponentType) => {
    const sd = this.screenContext.screenProvider.getScreenDesign();
    let found = false;
    sd.displayItems.forEach((di) => {
      if (di.id === specialDIId) found = true;
    });
    if (found) {
      KMainFunctions.displayWarningNotification("There is already this element. So, you can't place more.");
      return;
    }
    const dashboardState = this.screenContext.designSituationProvider?.state?.dashboardState;
    const isReportDesignComponentAllowed = dashboardState === DashboardState.reportDesigner;
    const component = this.screenContext.state.project.componentSetObj.components.find(
      (x) =>
        x.id === id &&
        x.componentType === componentType &&
        (isReportDesignComponentAllowed ? x.isReportDesignComponent === true : !x.isReportDesignComponent)
    );
    if (component) {
      const newComponent: IDisplayItemComponent | undefined = ScreenDesignHelper.createNewComponentById(
        id,
        this.screenContext
      );
      if (newComponent) {
        const newComponentDefinition: IComponentDefinition | undefined = this.getComponentDefinition(newComponent)!;
        let newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
          sd,
          this.screenContext.screenProvider.getScreenDesignDictionary(),
          undefined,
          newComponent,
          this.screenContext.state.project.componentSetObj.components
        );
        newDI.id = specialDIId;
        newDI = ScreenDesignHelper.addDefaultComponentStylings(
          newDI,
          newComponentDefinition,
          this.screenContext.defaultStyleProvider
        );
        sd.displayItems.splice(0, 0, newDI);
        this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropDi, specialDIId.toString());
        if (sd.isAbsoluteDesign === true) {
          if (newDI.styleObj?.position && !newDI.styleObj.position.height) {
            newDI.styleObj.position.height = 96;
          }
        }
      }
    }
  };

  changePosition = (kuikaid: string, fixedPositionInfo: IFixedPosition, absolutePositionInfo: IAbsolutePosition) => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    const sd: IScreenDesign = this.screenContext.screenProvider.getScreenDesign();
    const di = dict[parseInt(kuikaid, 10)];

    if (!di) {
      return;
    }

    const parentId = this.getParentId(di, sd.displayItems);
    const parentDI = parentId ? dict[parentId] : undefined;

    if (di && KMainFunctions.isObjectEmpty(di) === false) {
      di.fixedPositionInfo = _.cloneDeep(fixedPositionInfo);
      di.absolutePositionInfo = _.cloneDeep(absolutePositionInfo);
      this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropDi, kuikaid);
      const isElementHeightHigherThanContainer = KMainFunctions.getElementHeightIsHigherThanContainer(
        fixedPositionInfo,
        parentDI,
        sd,
        absolutePositionInfo
      );
      if (di?.styleObj?.position && fixedPositionInfo) {
        if (di.item.name !== "StaticTable") {
          di.styleObj.position.width = fixedPositionInfo?.width;
          di.styleObj.position.height = fixedPositionInfo?.height;
        }
        di.styleObj.position.xAxis = fixedPositionInfo?.left;
        di.styleObj.position.yAxis = fixedPositionInfo?.top;
      }
      const isHeightExpandableParent =
        parentDI?.item?.name === "Header" ||
        parentDI?.item?.name === "Footer" ||
        parentDI?.item?.name === "Page Header" ||
        parentDI?.item?.name === "Page Footer" ||
        parentDI?.item?.name === "Box" ||
        parentDI?.item?.name === "Panel";
      if (isElementHeightHigherThanContainer && isHeightExpandableParent) {
        if (
          isElementHeightHigherThanContainer &&
          parentDI?.styleObj?.position?.height &&
          fixedPositionInfo?.height &&
          fixedPositionInfo?.top
        ) {
          const diHeightDiffWithContainer =
            fixedPositionInfo?.height + fixedPositionInfo?.top - parentDI.styleObj?.position?.height;
          parentDI.styleObj.position.height += diHeightDiffWithContainer + 20;
          this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.ChangeStyleValue, kuikaid);
        }
      }
    }
  };

  copyItem = (kuikaid: string) => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    let clipboardDiObj: IDisplayItemClipboard;
    if (kuikaid === SpecialDIIds.rootDI.toString()) return;
    const sd: IScreenDesign = this.screenContext.screenProvider.getScreenDesign();
    if (!sd) {
      return;
    }

    const di = dict[parseInt(kuikaid, 10)];

    if (!di) {
      return;
    }

    const relatedNavigationDisplayItems = [] as IDisplayItem[];
    ScreenDesignHelper.findRelatedNavigationDisplayItems(di, sd.displayItems, relatedNavigationDisplayItems);

    clipboardDiObj = {
      displayItem: di,
      relatedNavigationComponents: relatedNavigationDisplayItems
    };

    this.screenContext.props.context.setClipboardValue("displayItem", clipboardDiObj);
  };

  pasteItem = (kuikaid: string) => {
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    const clipboard = this.screenContext.props.context.getClipboardValue("displayItem") as
      | IDisplayItemClipboard
      | string;
    if (clipboard === undefined || clipboard === "") {
      KMainFunctions.displayWarningNotification("Could not paste display item, because clipboard is empty");
      return;
    }

    const componentDefinition = this.getComponentDefinition((clipboard as any).displayItem?.item);
    const projectType = this.screenContext.state.project.projectType;
    // Same predicate as the palette: a component the palette would not offer on this screen cannot be
    // pasted onto it either. WebAndReactNative pastes when React Native generation is open to this user
    // (the union across platforms) — the publish validation (KV1263) is what tells a Native platform.
    const isPasteTargetSupported: boolean = isAppSupportTypeOfferedOnScreen({
      appSupportType: componentDefinition?.appSupportType,
      projectType,
      isWebScreen: this.screenContext.screenProvider.getIsWebScreen(),
      reactNativeOffered: isReactNativeGenerationOffered()
    });
    if (!isPasteTargetSupported) {
      if (componentDefinition?.appSupportType === EAppSupportType.Mobile) {
        KMainFunctions.displayWarningNotification("You can't paste mobile component to desktop project!");
      } else {
        KMainFunctions.displayWarningNotification("You can't paste desktop component to mobile project!");
      }
      return;
    }

    const isEditableTableColumnChild = CommonHelperFunctions.getIsEditableTableColumnChild(kuikaid, dict);
    if (
      isEditableTableColumnChild ||
      componentDefinition?.name === "EditableTableColumn" ||
      componentDefinition?.name === "TableDefaultColumn"
    ) {
      return;
    }

    const sd: IScreenDesign = this.screenContext.screenProvider.getScreenDesign();
    const sdDict = this.screenContext.screenProvider.getScreenDesignDictionary();
    if (!sd) {
      return;
    }

    const clipboardValue = _.cloneDeep(clipboard as IDisplayItemClipboard);

    const targetDI = sdDict[parseInt(kuikaid, 10)];
    const sourceDI = clipboardValue.displayItem;
    if (!clipboardValue.displayItem || !targetDI) {
      return;
    }

    const sourceDiCompDef = this.getComponentDefinition(clipboardValue.displayItem?.item) as IComponent;
    const targetDiCompDef = this.getComponentDefinition(targetDI?.item) as IComponent;
    if (!sourceDiCompDef || !targetDiCompDef) return;

    const generatedItems = ScreenDesignHelper.prepareDisplayItemPasteData(
      clipboardValue,
      sd,
      this.screenContext.screenProvider.getScreenDesignDictionary()
    );
    let newDI = generatedItems.displayItem;
    const navDIs = generatedItems.relatedNavigationComponents;
    const rootDiIdx = ScreenDesignHelper.getRootDiIdxInDisplayItems(sd.displayItems);
    const targetComponent: IDisplayItemComponent | undefined = targetDI?.item;

    if (
      sourceDiCompDef.componentType !== ComponentType.responsiveColumn &&
      sourceDiCompDef.componentType !== ComponentType.responsiveRow &&
      targetDI.id === -1 &&
      sourceDiCompDef.componentType !== ComponentType.fixedHeader &&
      sourceDiCompDef.componentType !== ComponentType.fixedFooter &&
      sourceDiCompDef.componentType !== ComponentType.layoutFooter &&
      sourceDiCompDef.componentType !== ComponentType.layoutHeader &&
      sourceDiCompDef.componentType !== ComponentType.layoutLeft &&
      sourceDiCompDef.componentType !== ComponentType.layoutRight
    ) {
      newDI = this.wrapDIWithDefaultRow(newDI, sd);
    }
    const message: IDragDropByMoveItemMessage = {
      insertMethod: DragDropInsertMethod.insertInto,
      sourceKuikaId: sourceDI.id.toString(),
      targetKuikaId: targetDI.id.toString()
    };
    const isElementPasteable = this.getElementIsDroppable(
      targetComponent,
      targetDiCompDef,
      sourceDiCompDef,
      targetDI,
      sd,
      sdDict,
      message,
      sourceDI
    );
    if (!isElementPasteable) {
      return;
    }

    navDIs?.forEach((navDI) => {
      if (rootDiIdx >= 0) sd.displayItems[rootDiIdx].children.push(navDI);
    });
    const parentId = this.getParentId(targetDI, sd.displayItems);
    const parentDi = parentId ? sdDict[parentId] : undefined;
    if (targetDI && targetDI.fixedPositionInfo && sd.isAbsoluteDesign === true) {
      let fixedPositionInfo = _.cloneDeep(targetDI.fixedPositionInfo);
      let absolutePositionInfo = _.cloneDeep(targetDI.absolutePositionInfo);
      const isHeightExpandableParent =
        parentDi?.item?.name === "Header" ||
        parentDi?.item?.name === "Footer" ||
        parentDi?.item?.name === "Page Header" ||
        parentDi?.item?.name === "Page Footer" ||
        parentDi?.item?.name === "Box" ||
        parentDi?.item?.name === "Panel";
      if (isHeightExpandableParent) {
        const isElementHeightHigherThanContainer = KMainFunctions.getElementHeightIsHigherThanContainer(
          fixedPositionInfo,
          parentDi,
          sd,
          absolutePositionInfo
        );
        if (isElementHeightHigherThanContainer && parentDi?.styleObj?.position?.height && fixedPositionInfo?.height) {
          parentDi.styleObj.position.height += fixedPositionInfo.height;
        }
      }
      newDI.fixedPositionInfo = fixedPositionInfo;
      newDI.absolutePositionInfo = absolutePositionInfo;
    }
    if (sd.isAbsoluteDesign === true) {
      if (newDI.fixedPositionInfo?.left) {
        newDI.fixedPositionInfo.left += newDI.fixedPositionInfo?.width ?? 0;
      }
      if (newDI.absolutePositionInfo?.left) {
        newDI.absolutePositionInfo.left += newDI.fixedPositionInfo?.width ?? 0;
      }
      if (newDI.fixedPositionInfo?.top) {
        newDI.fixedPositionInfo.top += newDI.fixedPositionInfo?.height ?? 0;
      }
      if (newDI.absolutePositionInfo?.top) {
        newDI.absolutePositionInfo.top += newDI.fixedPositionInfo?.height ?? 0;
      }
    }
    // if (targetDiCompDef.name === "TableRow" || targetDiCompDef.name === "ReportTableRow" || targetDiCompDef.name === "ReportTableHeader" || targetDiCompDef.name === "TableHeader") {
    //   KMainFunctions.displayWarningNotification("You can't paste display item here!");
    //   return;
    // }
    const itemDropBehavior = KAgentHelper.itemDropBehavior(sourceDiCompDef, targetDiCompDef);

    switch (itemDropBehavior) {
      case EItemDropBehavior.asLastChildItem:
        newDI.parentId = targetDI.id;
        this.placeToLastChildItem(sd, targetDI, newDI);
        break;
      case EItemDropBehavior.afterItem:
        newDI.parentId = this.getParentId(targetDI, sd.displayItems);
        const nextSelectedKuikaId = newDI.id.toString();
        this.placeAfterSpecificItem(sd, targetDI, newDI, nextSelectedKuikaId);
        break;
      case EItemDropBehavior.asFixedHeader:
        let headerFound = false;
        sd.displayItems.forEach((di) => {
          if (di.id === SpecialDIIds.headerDI) headerFound = true;
        });
        if (headerFound) {
          KMainFunctions.displayWarningNotification("There is already a header item. So, you can't place more.");
          return;
        }

        newDI.id = SpecialDIIds.headerDI;
        newDI.parentId = undefined;
        newDI.children.map((x) => (x.parentId = newDI.id));
        sd.displayItems.splice(0, 0, newDI);
        this.screenContext.screenProvider.setScreenDesign(
          sd,
          ESdChangeReason.DragDropDi,
          SpecialDIIds.headerDI.toString()
        );
        break;

      case EItemDropBehavior.asFixedFooter:
        let footerFound = false;
        sd.displayItems.forEach((di) => {
          if (di.id === SpecialDIIds.footerDI) footerFound = true;
        });
        if (footerFound) {
          KMainFunctions.displayWarningNotification("There is already a footer item. So, you can't place more.");
          return;
        }

        newDI.id = SpecialDIIds.footerDI;
        newDI.parentId = undefined;
        newDI.children.map((x) => (x.parentId = newDI.id));
        sd.displayItems.splice(0, 0, newDI);
        this.screenContext.screenProvider.setScreenDesign(
          sd,
          ESdChangeReason.DragDropDi,
          SpecialDIIds.footerDI.toString()
        );
        break;

      case EItemDropBehavior.asPageHeader:
        let pageHeaderFound = false;
        sd.displayItems.forEach((di) => {
          if (di.id === SpecialDIIds.pageHeaderDI) pageHeaderFound = true;
        });
        if (pageHeaderFound) {
          KMainFunctions.displayWarningNotification("There is already a page header item. So, you can't place more.");
          return;
        }

        newDI.id = SpecialDIIds.pageHeaderDI;
        newDI.parentId = undefined;
        newDI.children.map((x) => (x.parentId = newDI.id));
        sd.displayItems.splice(0, 0, newDI);
        this.screenContext.screenProvider.setScreenDesign(
          sd,
          ESdChangeReason.DragDropDi,
          SpecialDIIds.pageHeaderDI.toString()
        );
        break;

      case EItemDropBehavior.asPageFooter:
        let pageFooterFound = false;
        sd.displayItems.forEach((di) => {
          if (di.id === SpecialDIIds.pageFooterDI) pageFooterFound = true;
        });
        if (pageFooterFound) {
          KMainFunctions.displayWarningNotification("There is already a page footer item. So, you can't place more.");
          return;
        }

        newDI.id = SpecialDIIds.pageFooterDI;
        newDI.parentId = undefined;
        newDI.children.map((x) => (x.parentId = newDI.id));
        sd.displayItems.splice(0, 0, newDI);
        this.screenContext.screenProvider.setScreenDesign(
          sd,
          ESdChangeReason.DragDropDi,
          SpecialDIIds.pageFooterDI.toString()
        );
        break;
      default:
        KMainFunctions.displayWarningNotification("You can't paste display item here!");
        break;
    }
  };

  pasteItemStyle = (kuikaid: string) => {
    const clipboard = this.screenContext.props.context.getClipboardValue("displayItem") as
      | IDisplayItemClipboard
      | string;
    if (clipboard === undefined || clipboard === "") {
      KMainFunctions.displayWarningNotification("Could not paste styling, because clipboard is empty");
      return;
    }

    const sd: IScreenDesign = this.screenContext.screenProvider.getScreenDesign();
    const sdDict = this.screenContext.screenProvider.getScreenDesignDictionary();
    if (!sd) {
      return;
    }

    const clipboardValue = _.cloneDeep(clipboard as IDisplayItemClipboard);
    const targetDI = sdDict[parseInt(kuikaid, 10)];

    if (!clipboardValue.displayItem.styleObj || !targetDI) {
      return;
    }

    if (!targetDI.styleObj) {
      targetDI.styleObj = {};
    }

    const diItem = clipboardValue.displayItem.item;
    const targetDiItem = targetDI.item;

    if (diItem.name !== targetDiItem.name) {
      KMainFunctions.displayWarningNotification("You can only paste the style of the same component.");
      return;
    }

    const targetPosition = targetDI.styleObj.position
      ? { xAxis: targetDI.styleObj.position.xAxis, yAxis: targetDI.styleObj.position.yAxis }
      : undefined;

    Object.assign(targetDI.styleObj, clipboardValue.displayItem.styleObj);

    if (targetPosition && targetDI.styleObj.position) {
      targetDI.styleObj.position.xAxis = targetPosition.xAxis;
      targetDI.styleObj.position.yAxis = targetPosition.yAxis;
    }

    this.screenContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropDi, kuikaid);
  };

  getClipboardValue = () => {
    const clipboard = this.screenContext.props.context.getClipboardValue("displayItem") as
      | IDisplayItemClipboard
      | string;
    const sd: IScreenDesign = this.screenContext.screenProvider.getScreenDesign();
    if (!sd) {
      return;
    }

    const clipboardValue = _.cloneDeep(clipboard as IDisplayItemClipboard);
    return clipboardValue;
  };

  /**
   * Creates dynamic widget properties and events from resolved props.
   * All props (including events) are added to the component's properties array.
   * Events are stored as properties with selectedType = EPropertyType.event,
   * which makes them available in the event picker dropdown.
   */
  createWidgetDynamicPropertiesAndEvents = (
    component: IDisplayItemComponent,
    resolvedProps: IKuikaTypeResolution[]
  ) => {
    for (const prop of resolvedProps) {
      // Both regular properties and events are added as properties
      // Events use selectedType = EPropertyType.event
      this.addWidgetProperty(component, prop);
    }
  };

  /**
   * Adds a widget property to the component.
   */
  private addWidgetProperty = (component: IDisplayItemComponent, prop: IKuikaTypeResolution) => {
    // Skip if property already exists (e.g., technical props like framework, packId, componentId)
    if (component.properties.find((p) => p.name === prop.propName)) {
      return;
    }

    const selectedType = this.mapResolvedPropToPropertyType(prop);
    const symbol: ISymbol = {
      category: ESymbolCategory.fixed,
      label: prop.propName,
      description: "",
      fixedValue: this.getDefaultValueForProp(prop),
      isFixed: true
    };

    const property: IDisplayItemProperty = {
      name: prop.propName,
      symbol,
      selectedType,
      isDynamicProperty: true,
      dynamicPropertyDefinition: {
        name: prop.propName,
        type: [selectedType],
        options: prop.allowedValues ?? [],
        hasOptions: (prop.allowedValues?.length ?? 0) > 0,
        description: "",
        typeOptions: [],
        isRequired: prop.isRequired,
        isTechnical: false,
        isAdvanced: false,
        isIgnoredOnCodeOutput: false,
        isIgnoredOnGeneratedApp: false,
        tagName: "",
        defaultValue: String(this.getDefaultValueForProp(prop) ?? ""),
        enumType: this.mapKuikaTypeNameToEnumType(prop),
        kuikaTypeName: prop.kuikaTypeName,
        isDomainType: prop.isDomainType,
        domainTypeName: prop.domainTypeName,
        isList: prop.isList
      }
    };

    component.properties.push(property);
  };

  private mapKuikaTypeNameToEnumType = (prop: IKuikaTypeResolution): EEnumType => {
    switch (prop.kuikaTypeName?.toLowerCase()) {
      case "string":
        return EEnumType.string;
      case "double":
      case "number":
        return EEnumType.number;
      case "boolean":
        return EEnumType.boolean;
      default:
        return EEnumType.object;
    }
  };

  /**
   * Maps a resolved prop to the corresponding EPropertyType.
   */
  private mapResolvedPropToPropertyType = (prop: IKuikaTypeResolution): EPropertyType => {
    // Events are stored as properties with selectedType = event
    // This makes them appear in the event picker dropdown
    if (prop.isEvent) {
      return EPropertyType.event;
    }

    // Use the propertyType from the backend if available (but not for events)
    if (prop.propertyType && prop.propertyType !== EPropertyType.event) {
      return prop.propertyType as EPropertyType;
    }

    // Fallback mapping based on kuikaTypeName
    switch (prop.kuikaTypeName?.toLowerCase()) {
      case "string":
        return EPropertyType.string;
      case "double":
      case "number":
        return EPropertyType.number;
      case "boolean":
        return EPropertyType.boolean;
      case "datetime":
        return EPropertyType.dateTime;
      default:
        if (prop.isDomainType) {
          return EPropertyType.object;
        }
        if (prop.allowedValues && prop.allowedValues.length > 0) {
          return EPropertyType.enum;
        }
        return EPropertyType.string;
    }
  };

  /**
   * Gets the default value for a widget property based on its type.
   */
  private getDefaultValueForProp = (prop: IKuikaTypeResolution): unknown => {
    if (prop.isList) {
      return [];
    }
    if (prop.defaultValue) {
      return prop.defaultValue;
    }
    if (prop.allowedValues && prop.allowedValues.length > 0) {
      return prop.allowedValues[0];
    }
    switch (prop.kuikaTypeName?.toLowerCase()) {
      case "string":
        return "";
      case "double":
      case "number":
        return 0;
      case "boolean":
        return false;
      default:
        if (prop.allowedValues && prop.allowedValues.length > 0) {
          return prop.allowedValues[0];
        }
        return null;
    }
  };
}
